import axiosInstance, { axiosPublicInstance } from "../config/axiosApi/axiosApi";
import APIs from "../Constants/apis";
import {
  applyPerItemCustomPriceIfNeeded,
  chargesFreeSlotItem,
  dealHasSlotLevelPricing,
  getPerItemCustomPrice,
  getRealDealSlotSelectedItems,
  isFixedPricePerItemType,
  isFixedPricePerSlotType,
  parseSlotCustomPrice,
  resolveDealSlotPricing,
  shouldAutoDuplicateSlot,
} from "../utils/dealSlotPricing";
import { joinSelectedAllergenSolutions } from "../utils/allergenSolutions";
import { mapCartItemsForRewardValidation, normalizeReward } from "../utils/rewardUtils";
import {
  findOrderByCustomerReference,
  isFrontOrderReference,
  normalizeOrderReference,
} from "../utils/orderNumberDisplay";
import { normalizeSeatNo, seatDisplayName } from "../utils/seatUtils";
import { normalizeVoucher, computeVoucherDiscount } from "../utils/voucherStorage";
import { resolveImageUrl } from "../utils/imageUtils";

const customerAxios = axiosInstance;
const publicAxios = axiosPublicInstance;

/** Parse HTTP 409 price-mismatch payload from place-order / prepare-checkout. */
function extractPriceMismatch(errorOrResponse) {
  const response = errorOrResponse?.response || errorOrResponse;
  const status = response?.status ?? errorOrResponse?.status;
  if (status !== 409) return null;

  const body = response?.data || {};
  const data = body.data || body.Data || {};
  const serverTotal = Number(data.serverTotal ?? data.ServerTotal);
  if (!Number.isFinite(serverTotal)) return null;

  return {
    priceMismatch: true,
    success: false,
    message:
      body.message ||
      body.Message ||
      "Order prices have changed. Please review the updated total and submit again.",
    clientExpectedTotal: Number(data.clientExpectedTotal ?? data.ClientExpectedTotal) || 0,
    serverTotal,
    linesSummary: data.linesSummary || data.LinesSummary || null,
  };
}

/** Per order line; sent as JSON string matching C# Guid / SQL uniqueidentifier. Server assigns if all-zero Guid. */
function newRecvorderDetailGuid() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** Before POST: every line must have a real GUID string for C# `Guid` / SQL uniqueidentifier (except all-zero = server assigns). */
function ensureRecvorderDetailGuidsForPayload(details) {
  const uuidLike = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const emptyGuid = "00000000-0000-0000-0000-000000000000";
  (details || []).forEach((row) => {
    if (!row || typeof row !== "object") return;
    const raw = row.RecvorderDetail_ID;
    const s = raw === null || raw === undefined ? "" : String(raw).trim();
    if (s === emptyGuid) return;
    if (!s || s === "0" || !uuidLike.test(s)) {
      row.RecvorderDetail_ID = newRecvorderDetailGuid();
    }
  });
}

/** Pull reporting attribution fields from catalog / cart sources (API may use either casing). */
function pickReportFields(...sources) {
  const toInt = (v) => {
    if (v === null || v === undefined || v === "") return 0;
    const n = typeof v === "number" ? v : parseInt(v, 10);
    return Number.isFinite(n) ? n : 0;
  };
  const toNum = (v) => {
    if (v === null || v === undefined || v === "") return 0;
    const n = typeof v === "number" ? v : parseFloat(v);
    return Number.isFinite(n) ? n : 0;
  };
  let reportCatId = 0;
  let menuId = 0;
  let menuGroupId = 0;
  let costPrice = 0;
  let retailTax = 0;
  let taxExempt = false;
  for (const src of sources) {
    if (!src || typeof src !== "object") continue;
    if (reportCatId <= 0) {
      reportCatId = toInt(src.ReportCat_ID ?? src.reportCatId ?? src.reportCat_ID);
    }
    if (menuId <= 0) {
      menuId = toInt(src.MenuID ?? src.menuId ?? src.Menu_ID ?? src.menu_ID);
    }
    if (menuGroupId <= 0) {
      menuGroupId = toInt(src.MenuGroupID ?? src.menuGroupId ?? src.MenuGroup_ID ?? src.menuGroup_ID);
    }
    if (costPrice <= 0) {
      costPrice = toNum(src.MenuItem_CostPrice ?? src.costPrice ?? src.CostPrice);
    }
    const exemptFlag =
      src.isTaxExempt ?? src.IsTaxExempt ?? src.taxExempt;
    if (
      exemptFlag === true ||
      exemptFlag === 1 ||
      exemptFlag === "1" ||
      String(exemptFlag).toLowerCase() === "true"
    ) {
      taxExempt = true;
    }
    if (retailTax <= 0) {
      retailTax = toNum(src.Retail_Tax ?? src.taxPercentage ?? src.MenuItem_TaxPercentage ?? src.TaxPercentage);
    }
  }
  return {
    ReportCat_ID: reportCatId,
    MenuID: menuId,
    MenuGroupID: menuGroupId,
    MenuItem_CostPrice: costPrice,
    Retail_Tax: taxExempt ? 0 : retailTax,
  };
}

/**
 * When cart modifier objects omit MenuItem_ID, resolve from the line item's modifierGroups (same source as Menu.jsx).
 */
function lookupModifierMenuItemIdFromCartLine(cartLineItem, modifierName) {
  if (!cartLineItem || !modifierName) return 0;
  const target = String(modifierName).trim().toLowerCase();
  const groups = cartLineItem.modifierGroups ?? cartLineItem.ModifierGroups;
  if (!Array.isArray(groups)) return 0;
  for (const g of groups) {
    const mods = g.modifiers || g.Modifiers || [];
    if (!Array.isArray(mods)) continue;
    for (const m of mods) {
      const nm = String(m.modifierName ?? m.ModifierName ?? m.name ?? m.Name ?? "").trim().toLowerCase();
      if (nm !== target) continue;
      const candidates = [
        m.MenuItem_ID,
        m.menuItem_ID,
        m.MenuItem_Id,
        m.itemId,
        m.ItemId,
        m.modifierId,
        m.ModifierId,
        m.id,
      ];
      for (const c of candidates) {
        if (c === null || c === undefined || c === "") continue;
        const n = typeof c === "number" ? c : parseInt(String(c), 10);
        if (Number.isFinite(n) && n > 0) return n;
      }
    }
  }
  return 0;
}

/**
 * Resolve modifier group id/name for an order-detail MODIFIER row.
 * Prefer values stored on the cart modifier; fall back to the item's modifierGroups catalog.
 */
function resolveModifierGroupInfo(modifier, cartLineItem = null) {
  const toIntOrNull = (v) => {
    if (v === null || v === undefined || v === "") return null;
    const n =
      typeof v === "number"
        ? v
        : parseInt(String(v).replace(/^modifier-/i, ""), 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  };
  const cleanName = (v) => {
    const s = String(v ?? "").trim();
    return s || null;
  };

  let groupId = toIntOrNull(
    modifier?.modifierGroupID ??
      modifier?.modifierGroupId ??
      modifier?.ModifierGroup_ID ??
      modifier?.groupId ??
      modifier?.groupID ??
      modifier?.originalModifier?.modifierGroupID ??
      modifier?.originalModifier?.modifierGroupId ??
      modifier?.originalModifier?.ModifierGroup_ID
  );
  let groupName = cleanName(
    modifier?.modifierGroupName ??
      modifier?.groupName ??
      modifier?.groupTitle ??
      modifier?.originalModifier?.modifierGroupName ??
      modifier?.originalModifier?.ModifierGroupName
  );

  if ((!groupId || !groupName) && cartLineItem && modifier?.name) {
    const target = String(modifier.name).trim().toLowerCase();
    const groups =
      cartLineItem.modifierGroups ||
      cartLineItem.originalItem?.modifierGroups ||
      [];
    if (Array.isArray(groups)) {
      for (const g of groups) {
        const mods = g?.modifiers || g?.Modifiers || [];
        if (!Array.isArray(mods)) continue;
        const hit = mods.some((m) => {
          const nm = String(m?.modifierName ?? m?.name ?? m?.Name ?? "")
            .trim()
            .toLowerCase();
          return nm === target;
        });
        if (!hit) continue;
        if (!groupId) {
          groupId = toIntOrNull(
            g.modifierGroupId ?? g.modifierGroupID ?? g.ModifierGroup_ID ?? g.id
          );
        }
        if (!groupName) {
          groupName = cleanName(
            g.modifierGroupName ??
              g.ModifierGroupName ??
              g.modifierGroupSubTitle ??
              g.title ??
              g.name
          );
        }
        break;
      }
    }
  }

  return {
    ModifierGroupID: groupId,
    ModifierGroupName: groupName,
  };
}

/**
 * Service to handle online ordering API calls
 * All APIs are public (no authentication required)
 */
/**
 * Short-lived cache + in-flight de-duplication for read-only menu/deal GETs.
 * Deal selector -> summary -> menu all request the same data; without this every page
 * re-downloads (and the API re-builds) it. Failures / empty results are never cached, and every
 * caller receives its own copy so UI code can't corrupt the shared cache by mutating it.
 */
const API_CACHE_TTL_MS = 60_000;
const API_CACHE_MAX_ENTRIES = 200;
const apiResponseCache = new Map();
const apiResponseInflight = new Map();

const cloneCachedData = (value) => {
  try {
    return typeof structuredClone === "function" ? structuredClone(value) : JSON.parse(JSON.stringify(value));
  } catch {
    return value;
  }
};

const isCacheableApiData = (data) => {
  if (Array.isArray(data)) return data.length > 0;
  return data !== null && data !== undefined && typeof data === "object";
};

async function cachedApiGet(key, loader) {
  const hit = apiResponseCache.get(key);
  if (hit) {
    if (Date.now() - hit.at <= API_CACHE_TTL_MS) {
      return cloneCachedData(hit.data);
    }
    apiResponseCache.delete(key);
  }

  let inflight = apiResponseInflight.get(key);
  if (!inflight) {
    inflight = (async () => {
      const data = await loader();
      if (isCacheableApiData(data)) {
        if (apiResponseCache.size >= API_CACHE_MAX_ENTRIES) {
          const oldestKey = apiResponseCache.keys().next().value;
          apiResponseCache.delete(oldestKey);
        }
        apiResponseCache.set(key, { at: Date.now(), data });
      }
      return data;
    })().finally(() => {
      apiResponseInflight.delete(key);
    });
    apiResponseInflight.set(key, inflight);
  }

  return cloneCachedData(await inflight);
}

const apiCacheKey = (name, slug, ...parts) =>
  [name, String(slug || "").toLowerCase(), ...parts.map((p) => (p === null || p === undefined ? "" : String(p)))].join("|");

export const onlineOrderingService = {
  /**
   * Get restaurant information by slug
   * @param {string} restaurantSlug - Restaurant slug from URL
   * @param {number|null} locationId - Optional location ID for franchise/location
   * @returns {Promise<Object>} Restaurant information
   */
  async getRestaurantBySlug(restaurantSlug, locationId = null) {
    try {
      if (!restaurantSlug) {
        throw new Error("Restaurant slug is required");
      }

      const apiUrl = APIs.GET_RESTAURANT_BY_SLUG(restaurantSlug, locationId);
      console.log("getRestaurantBySlug - restaurantSlug:", restaurantSlug, "locationId:", locationId, "apiUrl:", apiUrl);
      
      const response = await axiosInstance.get(apiUrl);
      
      if (response.data.status && response.data.data) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || "Failed to fetch restaurant information");
      }
    } catch (error) {
      console.error("Error fetching restaurant by slug:", error);
      throw error;
    }
  },

  /**
   * Get all locations/franchises for a restaurant by slug
   * @param {string} restaurantSlug - Restaurant slug from URL
   * @returns {Promise<Array>} Array of location objects
   */
  async getRestaurantLocations(restaurantSlug) {
    try {
      if (!restaurantSlug) {
        throw new Error("Restaurant slug is required");
      }

      const response = await axiosInstance.get(APIs.GET_RESTAURANT_LOCATIONS_BY_SLUG(restaurantSlug));
      
      if (response.data.status && response.data.data) {
        return Array.isArray(response.data.data) ? response.data.data : [];
      } else {
        return [];
      }
    } catch (error) {
      console.error("Error fetching restaurant locations:", error);
      return [];
    }
  },

  /**
   * Get all countries for dropdown
   * @returns {Promise<Array>} Array of country objects with code and name
   */
  async getCountries() {
    try {
      const response = await axiosInstance.get(APIs.GET_ONLINE_ORDERING_COUNTRIES());
      
      if (response.data.status && response.data.data) {
        return Array.isArray(response.data.data) ? response.data.data : [];
      } else {
        return [];
      }
    } catch (error) {
      console.error("Error fetching countries:", error);
      return [];
    }
  },

  /**
   * Validate voucher code and get discount information
   * @param {string} restaurantSlug - Restaurant slug
   * @param {string} voucherCode - Voucher code to validate
   * @param {number} orderTotal - Optional order total for validation
   * @param {number} locationId - Optional location ID for child companies/franchises
   * @returns {Promise<Object>} Voucher information with discount amount
   */
  async validateVoucher(restaurantSlug, voucherCode, orderTotal = null, locationId = null) {
    try {
      if (!restaurantSlug || !voucherCode) {
        throw new Error("Restaurant slug and voucher code are required");
      }

      const apiUrl = APIs.VALIDATE_VOUCHER(restaurantSlug, voucherCode, orderTotal, locationId);
      const response = await axiosInstance.get(apiUrl);
      const body = response.data ?? {};
      const ok = body.status === true || body.Status === true;
      const payload = body.data ?? body.Data;

      if (ok && payload) {
        const voucher = normalizeVoucher(payload);
        if (orderTotal != null && Number(orderTotal) > 0 && voucher) {
          voucher.discountAmount = computeVoucherDiscount(voucher, orderTotal);
        }
        return {
          success: true,
          voucher,
        };
      }
      return {
        success: false,
        message: body.message || body.Message || "Invalid voucher code",
      };
    } catch (error) {
      console.error("Error validating voucher:", error);
      const errorMessage = error.response?.data?.message || error.message || "Failed to validate voucher";
      return {
        success: false,
        message: errorMessage
      };
    }
  },

  /**
   * Check order-count discount eligibility for the current customer.
   */
  async validateOrderCountDiscount(
    restaurantSlug,
    { orderTotal = null, locationId = null, phone = null, customerGuid = null } = {}
  ) {
    try {
      if (!restaurantSlug) {
        throw new Error("Restaurant slug is required");
      }

      const apiUrl = APIs.VALIDATE_ORDER_COUNT_DISCOUNT(restaurantSlug, {
        orderTotal,
        locationId,
        phone,
        customerGuid,
      });
      const response = await axiosInstance.get(apiUrl);
      const body = response.data ?? {};
      const ok = body.status === true || body.Status === true;
      const payload = body.data ?? body.Data ?? {};

      if (!ok) {
        return {
          success: false,
          eligible: false,
          discountAmount: 0,
          message: body.message || body.Message || "Failed to check order-count discount",
        };
      }

      const eligible = Boolean(payload.eligible ?? payload.Eligible);
      return {
        success: true,
        eligible,
        discountId: Number(payload.discountId ?? payload.DiscountId) || 0,
        discountName: payload.discountName ?? payload.DiscountName ?? "",
        discountPercentage: Number(payload.discountPercentage ?? payload.DiscountPercentage) || 0,
        discountAmount: Number(payload.discountAmount ?? payload.DiscountAmount) || 0,
        orderCountThreshold: Number(payload.orderCountThreshold ?? payload.OrderCountThreshold) || 0,
        orderCountCompareType: payload.orderCountCompareType ?? payload.OrderCountCompareType ?? "Equals",
        customerOrderCount: Number(payload.customerOrderCount ?? payload.CustomerOrderCount) || 0,
        message: body.message || body.Message || "",
      };
    } catch (error) {
      console.error("Error validating order-count discount:", error);
      return {
        success: false,
        eligible: false,
        discountAmount: 0,
        message: error.response?.data?.message || error.message || "Failed to check order-count discount",
      };
    }
  },

  /**
   * Image banners plus live online-ordering discount offers for the public site.
   * Uses /banners (existing) and /promos (offers + banners) so either API version works.
   */
  async getOnlineOrderingPromos(restaurantSlug, locationId = null) {
    const empty = { banners: [], offers: [] };
    if (!restaurantSlug) return empty;

    const parseOk = (response) => {
      const status = response?.status;
      if (status != null && status >= 400) return { ok: false, payload: null };
      const body = response?.data ?? {};
      const ok = body.status === true || body.Status === true;
      return { ok, payload: body.data ?? body.Data ?? null };
    };

    const resolveMediaUrl = (url) => {
      // Prefer shared resolver: relative / localhost paths use production media in development.
      return resolveImageUrl(url);
    };

    const mapBanners = (raw) =>
      (Array.isArray(raw) ? raw : [])
        .map((b) => ({
          bannerId: Number(b.bannerId ?? b.BannerId ?? b.onlineOrderingBanner_ID) || 0,
          title: String(b.title ?? b.Title ?? "").trim(),
          linkUrl: String(b.linkUrl ?? b.LinkUrl ?? "").trim(),
          sortOrder: Number(b.sortOrder ?? b.SortOrder) || 0,
          imageUrl: resolveMediaUrl(
            b.imageUrl ?? b.ImageUrl ?? b.fileUrl ?? b.FileUrl ?? b.filePath ?? b.FilePath ?? ""
          ),
        }))
        .filter((b) => b.imageUrl)
        .sort((a, b) => a.sortOrder - b.sortOrder || a.bannerId - b.bannerId);

    const mapOffers = (raw) =>
      (Array.isArray(raw) ? raw : [])
        .map((o) => ({
          discountId: Number(o.discountId ?? o.DiscountId) || 0,
          discountName: String(o.discountName ?? o.DiscountName ?? "").trim(),
          discountPercentage: Number(o.discountPercentage ?? o.DiscountPercentage) || 0,
          isSetMinimumSpend: Boolean(o.isSetMinimumSpend ?? o.IsSetMinimumSpend),
          minimumSpendAmount: Number(o.minimumSpendAmount ?? o.MinimumSpendAmount) || 0,
          isSetMaxDiscountValue: Boolean(o.isSetMaxDiscountValue ?? o.IsSetMaxDiscountValue),
          maxDiscountValue: Number(o.maxDiscountValue ?? o.MaxDiscountValue) || 0,
          orderCountThreshold: Number(o.orderCountThreshold ?? o.OrderCountThreshold) || 0,
          orderCountCompareType: String(
            o.orderCountCompareType ?? o.OrderCountCompareType ?? "Equals"
          ),
        }))
        .filter((o) => o.discountPercentage > 0);

    try {
      const [promosResponse, bannersResponse] = await Promise.all([
        publicAxios
          .get(APIs.GET_ONLINE_ORDERING_PROMOS(restaurantSlug, locationId))
          .catch(() => null),
        publicAxios
          .get(APIs.GET_ONLINE_ORDERING_BANNERS(restaurantSlug, locationId))
          .catch(() => null),
      ]);

      const promos = parseOk(promosResponse);
      const bannersOnly = parseOk(bannersResponse);

      let rawBanners = [];
      let rawOffers = [];

      if (promos.ok && promos.payload) {
        if (Array.isArray(promos.payload)) {
          rawBanners = promos.payload;
        } else {
          rawBanners = promos.payload.banners ?? promos.payload.Banners ?? [];
          rawOffers = promos.payload.offers ?? promos.payload.Offers ?? [];
        }
      }

      if ((!rawBanners || rawBanners.length === 0) && bannersOnly.ok && bannersOnly.payload) {
        rawBanners = Array.isArray(bannersOnly.payload)
          ? bannersOnly.payload
          : bannersOnly.payload.banners ?? bannersOnly.payload.Banners ?? [];
      }

      return {
        banners: mapBanners(rawBanners),
        offers: mapOffers(rawOffers),
      };
    } catch (error) {
      console.error("Error fetching online ordering promos:", error);
      return empty;
    }
  },

  async getCustomerLoyaltyStatus(restaurantSlug, locationId = null, committedPoints = 0) {
    try {
      if (!restaurantSlug) throw new Error("Restaurant slug is required");
      const apiUrl = APIs.GET_CUSTOMER_LOYALTY(restaurantSlug, locationId, committedPoints);
      const response = await customerAxios.get(apiUrl);
      const body = response.data ?? {};
      const ok = body.status === true || body.Status === true;
      const payload = body.data ?? body.Data;
      if (ok && payload) {
        return { success: true, data: payload };
      }
      return {
        success: false,
        message: body.message || body.Message || "Failed to load loyalty status",
      };
    } catch (error) {
      console.error("Error fetching loyalty status:", error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || "Failed to load loyalty status",
      };
    }
  },

  async getAvailableRewards(restaurantSlug, locationId = null, committedPoints = 0) {
    try {
      if (!restaurantSlug) throw new Error("Restaurant slug is required");
      const apiUrl = APIs.GET_AVAILABLE_REWARDS(restaurantSlug, locationId, committedPoints);
      const response = await customerAxios.get(apiUrl);
      const body = response.data ?? {};
      const ok = body.status === true || body.Status === true;
      const payload = body.data ?? body.Data;
      if (ok && payload) {
        const rewards = (payload.rewards || payload.Rewards || []).map(normalizeReward).filter(Boolean);
        return {
          success: true,
          data: {
            ...payload,
            rewards,
          },
        };
      }
      return {
        success: false,
        message: body.message || body.Message || "Failed to load rewards",
      };
    } catch (error) {
      console.error("Error fetching rewards:", error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || "Failed to load rewards",
      };
    }
  },

  async validateRewardRedemption(restaurantSlug, payload, locationId = null) {
    try {
      if (!restaurantSlug) throw new Error("Restaurant slug is required");
      const apiUrl = APIs.VALIDATE_REWARD_REDEMPTION(restaurantSlug, locationId);
      const body = {
        rewardId: Number(payload.rewardId),
        orderSubtotal: Number(payload.orderSubtotal) || 0,
        committedPoints: Number(payload.committedPoints) || 0,
        cartItems: mapCartItemsForRewardValidation(payload.cartItems || []),
      };
      const response = await customerAxios.post(apiUrl, body);
      const resBody = response.data ?? {};
      const ok = resBody.status === true || resBody.Status === true;
      const data = resBody.data ?? resBody.Data;
      if (ok && data) {
        const reward = normalizeReward(data.reward ?? data.Reward);
        return {
          success: true,
          canRedeem: !!data.canRedeem,
          discountAmount: Number(data.discountAmount) || 0,
          currentPoints: Number(data.currentPoints) || 0,
          committedPoints: Number(data.committedPoints) || 0,
          remainingPoints: Number(data.remainingPoints) || 0,
          requiredPoints: Number(data.requiredPoints) || 0,
          pointsNeeded: Number(data.pointsNeeded) || 0,
          message: data.message || resBody.message || resBody.Message || "",
          requiresItemInCart: !!data.requiresItemInCart,
          missingItemName: data.missingItemName,
          missingItemId: data.missingItemId,
          reward,
        };
      }
      return {
        success: false,
        message: resBody.message || resBody.Message || data?.message || "Reward cannot be applied",
      };
    } catch (error) {
      console.error("Error validating reward:", error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || "Failed to validate reward",
      };
    }
  },

  /**
   * Check if restaurant is currently open for a specific order type
   * @param {string} restaurantSlug - Restaurant slug from URL
   * @param {string} orderType - Order type: "delivery", "collection", "dine-in", or "restaurant"
   * @param {number|null} locationId - Optional location ID for franchise/location
   * @returns {Promise<Object>} Availability status with isAvailable and message
   */
  async checkRestaurantAvailability(restaurantSlug, orderType, locationId = null) {
    try {
      if (!restaurantSlug || !orderType) {
        throw new Error("Restaurant slug and order type are required");
      }

      const apiUrl = APIs.CHECK_RESTAURANT_AVAILABILITY(restaurantSlug, orderType, locationId);
      const response = await axiosInstance.get(apiUrl);
      
      if (response.data.status && response.data.data) {
        return {
          success: true,
          isAvailable: response.data.data.isAvailable,
          message: response.data.data.message || "",
          openingTime: response.data.data.openingTime || "",
          closingTime: response.data.data.closingTime || ""
        };
      } else {
        return {
          success: true,
          isAvailable: true, // Default to available if check fails
          message: ""
        };
      }
    } catch (error) {
      console.error("Error checking restaurant availability:", error);
      // Default to available if check fails
      return {
        success: true,
        isAvailable: true,
        message: ""
      };
    }
  },

  /**
   * Get company settings for online ordering by restaurant slug
   * @param {string} restaurantSlug - Restaurant slug from URL
   * @returns {Promise<Object>} Company settings
   */
  async getCompanySettings(restaurantSlug) {
    try {
      if (!restaurantSlug) {
        throw new Error("Restaurant slug is required");
      }

      const response = await axiosInstance.get(APIs.GET_COMPANY_SETTINGS_BY_SLUG(restaurantSlug));
      
      if (response.data.status && response.data.data) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || "Failed to fetch company settings");
      }
    } catch (error) {
      console.error("Error fetching company settings by slug:", error);
      throw error;
    }
  },

  /**
   * Get all menu items by restaurant slug
   * @param {string} restaurantSlug - Restaurant slug from URL
   * @param {number|null} locationId - Optional location ID for franchise/location
   * @returns {Promise<Array>} Array of menu items
   */
  async getMenuBySlug(restaurantSlug, locationId = null) {
    try {
      if (!restaurantSlug) {
        throw new Error("Restaurant slug is required");
      }

      const response = await axiosInstance.get(APIs.GET_MENU_BY_SLUG(restaurantSlug, locationId));
      
      if (response.data.status && response.data.data) {
        return Array.isArray(response.data.data) ? response.data.data : [];
      } else {
        return [];
      }
    } catch (error) {
      console.error("Error fetching menu by slug:", error);
      return [];
    }
  },

  /**
   * Get all categories by restaurant slug
   * @param {string} restaurantSlug - Restaurant slug from URL
   * @param {number|null} locationId - Optional location ID for franchise/location
   * @returns {Promise<Array>} Array of categories
   */
  async getCategoriesBySlug(restaurantSlug, locationId = null) {
    try {
      if (!restaurantSlug) {
        throw new Error("Restaurant slug is required");
      }

      const response = await axiosInstance.get(APIs.GET_CATEGORIES_BY_SLUG(restaurantSlug, locationId));
      
      if (response.data.status && response.data.data) {
        return Array.isArray(response.data.data) ? response.data.data : [];
      } else {
        return [];
      }
    } catch (error) {
      console.error("Error fetching categories by slug:", error);
      return [];
    }
  },

  /**
   * Get menu items by category and restaurant slug
   * @param {string} restaurantSlug - Restaurant slug from URL
   * @param {number} categoryId - Category ID
   * @returns {Promise<Array>} Array of menu items for the category
   */
  async getCategoryItemsBySlug(restaurantSlug, categoryId, locationId = null) {
    try {
      if (!restaurantSlug || !categoryId) {
        throw new Error("Restaurant slug and category ID are required");
      }

      const response = await axiosInstance.get(
        APIs.GET_CATEGORY_ITEMS_BY_SLUG(restaurantSlug, categoryId, locationId)
      );
      
      if (response.data.status && response.data.data) {
        return Array.isArray(response.data.data) ? response.data.data : [];
      } else {
        return [];
      }
    } catch (error) {
      console.error("Error fetching category items by slug:", error);
      return [];
    }
  },

  /**
   * Get items for a category and all its subcategories by restaurant slug
   * Returns items grouped by category
   * @param {string} restaurantSlug - Restaurant slug from URL
   * @param {number} categoryId - Category ID
   * @returns {Promise<Array>} Array of { categoryId, categoryName, items[] }
   */
  async getCategoryAndSubcategoryItemsBySlug(restaurantSlug, categoryId, locationId = null) {
    try {
      if (!restaurantSlug || !categoryId) {
        throw new Error("Restaurant slug and category ID are required");
      }

      const response = await axiosInstance.get(
        APIs.GET_CATEGORY_AND_SUBCATEGORY_ITEMS_BY_SLUG(restaurantSlug, categoryId, locationId)
      );

      if (response.data.status && response.data.data) {
        return Array.isArray(response.data.data) ? response.data.data : [];
      } else {
        return [];
      }
    } catch (error) {
      console.error("Error fetching category and subcategory items by slug:", error);
      return [];
    }
  },

  /**
   * Search menu items by restaurant slug
   * @param {string} restaurantSlug - Restaurant slug from URL
   * @param {string} query - Search query (minimum 2 characters)
   * @returns {Promise<Array>} Array of matching menu items
   */
  async searchItemsBySlug(restaurantSlug, query) {
    try {
      if (!restaurantSlug) {
        throw new Error("Restaurant slug is required");
      }

      if (!query || query.length < 2) {
        return [];
      }

      const response = await axiosInstance.get(
        APIs.SEARCH_ITEMS_BY_SLUG(restaurantSlug, query)
      );
      
      if (response.data.status && response.data.data) {
        return Array.isArray(response.data.data) ? response.data.data : [];
      } else {
        return [];
      }
    } catch (error) {
      console.error("Error searching items by slug:", error);
      return [];
    }
  },

  /**
   * Get complete menu with all categories and items by restaurant slug
   * @param {string} restaurantSlug - Restaurant slug from URL
   * @returns {Promise<Object>} Full menu object with categories and items
   */
  async getFullMenuBySlug(restaurantSlug, locationId = null) {
    try {
      if (!restaurantSlug) {
        throw new Error("Restaurant slug is required");
      }

      const response = await axiosInstance.get(APIs.GET_FULL_MENU_BY_SLUG(restaurantSlug, locationId));
      
      if (response.data.status && response.data.data) {
        return response.data.data;
      } else {
        return {
          restaurantId: null,
          restaurantName: "",
          restaurantSlug: restaurantSlug,
          categories: []
        };
      }
    } catch (error) {
      console.error("Error fetching full menu by slug:", error);
      return {
        restaurantId: null,
        restaurantName: "",
        restaurantSlug: restaurantSlug,
        categories: []
      };
    }
  },

  /**
   * Get all combo items by restaurant slug with full details
   * Uses the dedicated combos API endpoint
   * @param {string} restaurantSlug - Restaurant slug from URL
   * @returns {Promise<Array>} Array of combo items with modifiers, variants, and prices
   */
  async getCombosBySlug(restaurantSlug, locationId = null) {
    try {
      if (!restaurantSlug) {
        throw new Error("Restaurant slug is required");
      }

      return await cachedApiGet(apiCacheKey("combos", restaurantSlug, locationId), async () => {
        const response = await axiosInstance.get(APIs.GET_COMBOS_BY_SLUG(restaurantSlug, locationId));

        if (response.data.status && response.data.data) {
          return Array.isArray(response.data.data) ? response.data.data : [];
        }
        return [];
      });
    } catch (error) {
      console.error("Error fetching combos by slug:", error);
      return [];
    }
  },

  /**
   * Get all products included in a combo/deal
   * @param {string} restaurantSlug - Restaurant slug from URL
   * @param {number} comboId - Combo/Deal ID
   * @param {number|null} locationId - Optional location ID for franchise/location
   * @returns {Promise<Array>} Array of products included in the combo
   */
  async getComboProducts(restaurantSlug, comboId, locationId = null) {
    try {
      if (!restaurantSlug || !comboId) {
        throw new Error("Restaurant slug and combo ID are required");
      }

      const response = await axiosInstance.get(APIs.GET_COMBO_PRODUCTS(restaurantSlug, comboId, locationId));
      
      if (response.data.status && response.data.data) {
        return Array.isArray(response.data.data) ? response.data.data : [];
      } else {
        return [];
      }
    } catch (error) {
      console.error("Error fetching combo products:", error);
      return [];
    }
  },

  /**
   * Get all active published deals by restaurant slug
   * @param {string} restaurantSlug - Restaurant slug from URL
   * @param {number|null} locationId - Optional location ID for franchise/location
   * @returns {Promise<Array>} Array of active published deals
   */
  async getDealsBySlug(restaurantSlug, locationId = null) {
    try {
      if (!restaurantSlug) {
        throw new Error("Restaurant slug is required");
      }

      return await cachedApiGet(apiCacheKey("deals", restaurantSlug, locationId), async () => {
        const response = await axiosInstance.get(APIs.GET_DEALS_BY_SLUG(restaurantSlug, locationId));

        if (response.data.status && response.data.data) {
          return Array.isArray(response.data.data) ? response.data.data : [];
        }
        return [];
      });
    } catch (error) {
      console.error("Error fetching deals by slug:", error);
      return [];
    }
  },

  /**
   * Get deal details with slots, allowed categories, and allowed items
   * @param {string} restaurantSlug - Restaurant slug from URL
   * @param {number} dealId - Deal ID
   * @param {number|null} locationId - Optional location ID for franchise/location
   * @returns {Promise<Object>} Deal details with slots information
   */
  async getDealDetailsBySlug(restaurantSlug, dealId, locationId = null) {
    try {
      if (!restaurantSlug || !dealId) {
        throw new Error("Restaurant slug and deal ID are required");
      }

      return await cachedApiGet(apiCacheKey("deal", restaurantSlug, dealId, locationId), async () => {
        const response = await axiosInstance.get(APIs.GET_DEAL_DETAILS_BY_SLUG(restaurantSlug, dealId, locationId));

        if (response.data.status && response.data.data) {
          return response.data.data;
        }
        throw new Error(response.data.message || "Failed to fetch deal details");
      });
    } catch (error) {
      console.error("Error fetching deal details by slug:", error);
      throw error;
    }
  },

  /**
   * Get available items for a deal slot
   * Returns items that can be selected for a specific slot based on allowed categories and items
   * @param {string} restaurantSlug - Restaurant slug from URL
   * @param {number} dealId - Deal ID
   * @param {number} slotId - Slot ID
   * @param {number|null} locationId - Optional location ID for franchise/location
   * @returns {Promise<Array>} Array of available items for the slot
   */
  async getDealSlotItemsBySlug(restaurantSlug, dealId, slotId, locationId = null) {
    try {
      if (!restaurantSlug || !dealId || !slotId) {
        throw new Error("Restaurant slug, deal ID, and slot ID are required");
      }

      return await cachedApiGet(apiCacheKey("dealSlotItems", restaurantSlug, dealId, slotId, locationId), async () => {
        const response = await axiosInstance.get(
          APIs.GET_DEAL_SLOT_ITEMS_BY_SLUG(restaurantSlug, dealId, slotId, locationId)
        );

        if (response.data.status && response.data.data) {
          return Array.isArray(response.data.data) ? response.data.data : [];
        }
        return [];
      });
    } catch (error) {
      console.error("Error fetching deal slot items by slug:", error);
      return [];
    }
  },

  /**
   * Get items with categories, modifiers, variants, and prices by restaurant slug
   * Optionally filter by category ID
   * @param {string} restaurantSlug - Restaurant slug from URL
   * @param {number|null} categoryId - Optional category ID to filter items
   * @param {number|null} locationId - Optional location ID for franchise/location
   * @returns {Promise<Array>} Array of items with full details
   */
  async getItemsWithDetailsBySlug(restaurantSlug, categoryId = null, locationId = null) {
    try {
      if (!restaurantSlug) {
        throw new Error("Restaurant slug is required");
      }

      return await cachedApiGet(apiCacheKey("itemsWithDetails", restaurantSlug, categoryId, locationId), async () => {
        const response = await axiosInstance.get(
          APIs.GET_ITEMS_WITH_DETAILS_BY_SLUG(restaurantSlug, categoryId, locationId)
        );

        const payload = response.data || {};
        const ok = payload.status === true || payload.Status === true;
        const data = payload.data !== undefined ? payload.data : payload.Data;

        if (ok && data) {
          return Array.isArray(data) ? data : [];
        }
        return [];
      });
    } catch (error) {
      console.error("Error fetching items with details by slug:", error);
      return [];
    }
  },

  /**
   * Get service areas with tables and seats by restaurant slug (public access)
   * @param {string} restaurantSlug - Restaurant slug from URL
   * @param {number|null} locationId - Optional location ID for franchise/location
   * @returns {Promise<Array>} Array of service areas with tables and seats
   */
  async getServiceAreasBySlug(restaurantSlug, locationId = null) {
    try {
      if (!restaurantSlug) {
        throw new Error("Restaurant slug is required");
      }

      const response = await axiosInstance.get(
        APIs.GET_SERVICE_AREAS_BY_SLUG(restaurantSlug, locationId)
      );
      
      if (response.data.status && response.data.data) {
        return Array.isArray(response.data.data) ? response.data.data : [];
      } else {
        return [];
      }
    } catch (error) {
      console.error("Error fetching service areas by slug:", error);
      return [];
    }
  },

  /**
   * Customer login (public)
   * @param {string} restaurantSlug - Restaurant slug
   * @param {Object} payload - { email, password }
   * @param {number|null} locationId - Optional location ID
   * @returns {Promise<Object>} { success, token, customer } or { success: false, error }
   */
  async customerLogin(restaurantSlug, payload, locationId = null) {
    try {
      if (!restaurantSlug || !payload?.email || !payload?.password) {
        return { success: false, error: "Email and password are required" };
      }
      const response = await publicAxios.post(
        APIs.LOGIN_CUSTOMER(restaurantSlug, locationId),
        { email: payload.email.trim(), password: payload.password }
      );
      const data = response.data?.data ?? response.data?.Data;
      if (response.data?.status && data) {
        const token = data.token ?? data.Token;
        const refreshToken = data.refreshToken ?? data.RefreshToken ?? "";
        const customer = data.customer ?? data.Customer;
        if (token && customer) return { success: true, token, refreshToken, customer };
      }
      return {
        success: false,
        error: (response.data?.message ?? response.data?.Message) || "Login failed",
      };
    } catch (err) {
      return {
        success: false,
        error: err.response?.data?.message || err.message || "Login failed",
      };
    }
  },

  /**
   * Customer sign up (public). Creates customer and returns token + customer for auto-login.
   */
  async customerSignup(restaurantSlug, payload, locationId = null) {
    try {
      if (
        !restaurantSlug ||
        !payload?.email ||
        !payload?.password ||
        !payload?.fullName ||
        !payload?.phone
      ) {
        return { success: false, error: "Email, password, full name and phone are required" };
      }
      const response = await publicAxios.post(
        APIs.SIGNUP_CUSTOMER(restaurantSlug, locationId),
        {
          email: payload.email.trim(),
          password: payload.password,
          fullName: payload.fullName.trim(),
          phone: payload.phone.trim(),
        }
      );
      const data = response.data?.data ?? response.data?.Data;
      if (response.data?.status && data) {
        const token = data.token ?? data.Token;
        const refreshToken = data.refreshToken ?? data.RefreshToken ?? "";
        const customer = data.customer ?? data.Customer;
        if (token && customer) return { success: true, token, refreshToken, customer };
      }
      return {
        success: false,
        error: (response.data?.message ?? response.data?.Message) || "Sign up failed",
      };
    } catch (err) {
      return {
        success: false,
        error: err.response?.data?.message || err.message || "Sign up failed",
      };
    }
  },

  /**
   * Refresh customer access token (public). Uses refresh token, or a legacy access token.
   */
  async customerRefreshToken(restaurantSlug, refreshToken, locationId = null) {
    try {
      if (!restaurantSlug || !refreshToken) {
        return { success: false, error: "Refresh token is required" };
      }
      const response = await publicAxios.post(
        APIs.REFRESH_CUSTOMER_TOKEN(restaurantSlug, locationId),
        { refreshToken }
      );
      const data = response.data?.data ?? response.data?.Data;
      if (response.data?.status && data) {
        const token = data.token ?? data.Token;
        const nextRefresh = data.refreshToken ?? data.RefreshToken ?? "";
        const customer = data.customer ?? data.Customer;
        if (token) return { success: true, token, refreshToken: nextRefresh, customer };
      }
      return {
        success: false,
        error: (response.data?.message ?? response.data?.Message) || "Token refresh failed",
      };
    } catch (err) {
      return {
        success: false,
        error: err.response?.data?.message || err.message || "Token refresh failed",
      };
    }
  },

  /**
   * Forgot password (public). Sends reset email.
   */
  async customerForgotPassword(restaurantSlug, email, locationId = null) {
    try {
      if (!restaurantSlug || !email?.trim()) {
        return { success: false, error: "Email is required" };
      }
      const response = await publicAxios.post(
        APIs.FORGOT_PASSWORD_CUSTOMER(restaurantSlug, locationId),
        { email: email.trim() }
      );
      if (response.data?.status) {
        return {
          success: true,
          message: response.data?.message ?? response.data?.Message,
        };
      }
      return {
        success: false,
        error: response.data?.message || "Could not send password reset email",
      };
    } catch (err) {
      return {
        success: false,
        error: err.response?.data?.message || err.message || "Request failed",
      };
    }
  },

  /**
   * Reset password (public) using email token.
   */
  async customerResetPassword(restaurantSlug, payload, locationId = null) {
    try {
      if (
        !restaurantSlug ||
        !payload?.token ||
        !payload?.newPassword ||
        !payload?.confirmPassword
      ) {
        return { success: false, error: "Token and passwords are required" };
      }

      const response = await publicAxios.post(
        APIs.RESET_PASSWORD_CUSTOMER(restaurantSlug, locationId),
        {
          token: payload.token,
          newPassword: payload.newPassword,
          confirmPassword: payload.confirmPassword,
        }
      );

      if (response.data?.status) {
        return {
          success: true,
          message: response.data?.message ?? "Password reset successful",
        };
      }

      return {
        success: false,
        error: response.data?.message || "Could not reset password",
      };
    } catch (err) {
      return {
        success: false,
        error: err.response?.data?.message || err.message || "Request failed",
      };
    }
  },

  /**
   * Get my orders (requires customer token; axios interceptor adds it from localStorage).
   */
  async getMyOrders(restaurantSlug, locationId = null) {
    try {
      if (!restaurantSlug) {
        throw new Error("Restaurant slug is required");
      }
      const response = await customerAxios.get(APIs.GET_MY_ORDERS(restaurantSlug, locationId));
      const data = response.data?.data ?? response.data?.Data;
      if (response.data?.status && Array.isArray(data)) {
        return data;
      }
      return [];
    } catch (err) {
      console.error("Error fetching my orders:", err);
      throw err;
    }
  },

  /**
   * Track order without login — ONL-* or invoice + phone used at checkout.
   */
  async getOrderByInvoiceGuest(restaurantSlug, invoiceNo, phone, locationId = null) {
    try {
      const reference =
        normalizeOrderReference(invoiceNo) || String(invoiceNo || "").trim();
      if (!restaurantSlug || !reference || !phone) {
        throw new Error("Restaurant, order number, and phone are required");
      }
      const response = await publicAxios.get(
        APIs.GUEST_TRACK_ORDER(restaurantSlug, reference, phone, locationId)
      );
      const httpStatus = response.status;
      const payload = response.data || {};
      const ok = payload.status ?? payload.Status;
      const data = payload.data ?? payload.Data;
      const message = payload.message || payload.Message || "Order not found";

      if (httpStatus >= 400 || ok === false) {
        const err = new Error(message);
        err.response = response;
        throw err;
      }
      if (ok && data) return data;
      const err = new Error(message);
      err.response = response;
      throw err;
    } catch (err) {
      console.error("Error fetching guest order:", err);
      throw err;
    }
  },

  /**
   * Track order — accepts customer-facing ONL-* or internal invoice.
   */
  async trackOrder(restaurantSlug, invoiceNo, { phone = "", locationId = null, isLoggedIn = false } = {}) {
    const trimmedInvoice =
      normalizeOrderReference(invoiceNo) || String(invoiceNo || "").trim();
    if (!restaurantSlug || !trimmedInvoice) {
      throw new Error("Restaurant and order number are required");
    }

    if (isLoggedIn) {
      try {
        return await this.getOrderByInvoice(restaurantSlug, trimmedInvoice, locationId);
      } catch (err) {
        const status = err.response?.status;

        // Retry without branch locationId (order may live on parent CompID)
        if (locationId != null && locationId > 0 && (status === 404 || !status)) {
          try {
            return await this.getOrderByInvoice(restaurantSlug, trimmedInvoice, null);
          } catch (_) {
            /* continue */
          }
        }

        // Resolve ONL-* via My Orders → internal invoice, then retry
        if (isFrontOrderReference(trimmedInvoice)) {
          try {
            const myOrders = await this.getMyOrders(restaurantSlug, locationId);
            const match = findOrderByCustomerReference(myOrders, trimmedInvoice);
            const invoice =
              match?.recvorder_InvoiceNo ?? match?.Recvorder_InvoiceNo ?? "";
            if (invoice && String(invoice).trim() !== trimmedInvoice) {
              return await this.getOrderByInvoice(
                restaurantSlug,
                String(invoice).trim(),
                locationId
              );
            }
          } catch (_) {
            /* keep original error */
          }
        }
        if (status !== 401 && status !== 403) {
          throw err;
        }
      }
    }

    const trimmedPhone = String(phone || "").trim();
    if (!trimmedPhone) {
      throw new Error("Please enter the phone number used when you placed the order");
    }
    return this.getOrderByInvoiceGuest(restaurantSlug, trimmedInvoice, trimmedPhone, locationId);
  },

  /**
   * Get order by invoice or front order id (ONL-*) for logged-in customer.
   */
  async getOrderByInvoice(restaurantSlug, invoiceNo, locationId = null) {
    try {
      const reference =
        normalizeOrderReference(invoiceNo) || String(invoiceNo || "").trim();
      if (!restaurantSlug || !reference) {
        throw new Error("Restaurant slug and order number are required");
      }
      const response = await customerAxios.get(
        APIs.GET_ORDER_BY_INVOICE(restaurantSlug, reference, locationId)
      );
      const httpStatus = response.status;
      const payload = response.data || {};
      const ok = payload.status ?? payload.Status;
      const data = payload.data ?? payload.Data;
      const message = payload.message || payload.Message || "Order not found";

      if (httpStatus >= 400 || ok === false) {
        const err = new Error(message);
        err.response = response;
        throw err;
      }
      if (ok && data) return data;
      const err = new Error(message);
      err.response = response;
      throw err;
    } catch (err) {
      console.error("Error fetching order by invoice:", err);
      throw err;
    }
  },

  /**
   * Get saved addresses for logged-in customer (requires customer token).
   * @param {string} restaurantSlug - Restaurant slug
   * @param {number|null} locationId - Optional location ID
   * @returns {Promise<Array>} List of saved addresses
   */
  async getCustomerAddresses(restaurantSlug, locationId = null) {
    try {
      if (!restaurantSlug) {
        throw new Error("Restaurant slug is required");
      }
      const response = await customerAxios.get(APIs.GET_CUSTOMER_ADDRESSES(restaurantSlug, locationId));
      const data = response.data?.data ?? response.data?.Data;
      if (response.data?.status && Array.isArray(data)) {
        return data;
      }
      return [];
    } catch (err) {
      console.error("Error fetching customer addresses:", err);
      throw err;
    }
  },

  /**
   * Add a saved address for logged-in customer (requires customer token).
   * @param {string} restaurantSlug - Restaurant slug
   * @param {Object} payload - { addressLine1, aptNo?, postalCode?, label?, isDefault? }
   * @param {number|null} locationId - Optional location ID
   * @returns {Promise<Object>} { shipmentAddressList_ID } or throws
   */
  async addCustomerAddress(restaurantSlug, payload, locationId = null) {
    try {
      if (!restaurantSlug) {
        throw new Error("Restaurant slug is required");
      }
      if (!payload?.addressLine1?.trim()) {
        throw new Error("Address line is required");
      }
      const body = {
        addressLine1: payload.addressLine1.trim(),
        aptNo: payload.aptNo ?? "",
        postalCode: payload.postalCode ?? "",
        label: payload.label ?? "",
        isDefault: !!payload.isDefault
      };
      const response = await customerAxios.post(
        APIs.POST_CUSTOMER_ADDRESS(restaurantSlug, locationId),
        body
      );
      if (response.data?.status && response.data?.data) {
        return response.data.data;
      }
      throw new Error(response.data?.message || "Failed to add address");
    } catch (err) {
      console.error("Error adding customer address:", err);
      throw err;
    }
  },

  /**
   * Update a saved address for logged-in customer (requires customer token).
   */
  async updateCustomerAddress(restaurantSlug, id, payload, locationId = null) {
    try {
      if (!restaurantSlug || !id) {
        throw new Error("Restaurant slug and address id are required");
      }
      if (!payload?.addressLine1?.trim()) {
        throw new Error("Address line is required");
      }
      const body = {
        addressLine1: payload.addressLine1.trim(),
        aptNo: payload.aptNo ?? "",
        postalCode: payload.postalCode ?? "",
        label: payload.label ?? "",
        isDefault: !!payload.isDefault
      };
      const response = await customerAxios.put(
        APIs.PUT_CUSTOMER_ADDRESS(restaurantSlug, id, locationId),
        body
      );
      if (response.data?.status) {
        return true;
      }
      throw new Error(response.data?.message || "Failed to update address");
    } catch (err) {
      console.error("Error updating customer address:", err);
      throw err;
    }
  },

  /**
   * Delete a saved address for logged-in customer (requires customer token).
   */
  async deleteCustomerAddress(restaurantSlug, id, locationId = null) {
    try {
      if (!restaurantSlug || !id) {
        throw new Error("Restaurant slug and address id are required");
      }
      const response = await customerAxios.delete(
        APIs.DELETE_CUSTOMER_ADDRESS(restaurantSlug, id, locationId)
      );
      if (response.data?.status) {
        return true;
      }
      throw new Error(response.data?.message || "Failed to delete address");
    } catch (err) {
      console.error("Error deleting customer address:", err);
      throw err;
    }
  },

  /**
   * Set a saved address as default for logged-in customer (requires customer token).
   */
  async setDefaultCustomerAddress(restaurantSlug, id, locationId = null) {
    try {
      if (!restaurantSlug || !id) {
        throw new Error("Restaurant slug and address id are required");
      }
      const response = await customerAxios.patch(
        APIs.PATCH_CUSTOMER_ADDRESS_SET_DEFAULT(restaurantSlug, id, locationId)
      );
      if (response.data?.status) {
        return true;
      }
      throw new Error(response.data?.message || "Failed to set default address");
    } catch (err) {
      console.error("Error setting default customer address:", err);
      throw err;
    }
  },

  /**
   * Get in-app notifications for logged-in customer (requires customer token).
   */
  async getCustomerNotifications(restaurantSlug, pageNumber = 1, pageSize = 20, locationId = null) {
    try {
      if (!restaurantSlug) {
        throw new Error("Restaurant slug is required");
      }
      const response = await customerAxios.get(
        APIs.GET_CUSTOMER_NOTIFICATIONS(restaurantSlug, pageNumber, pageSize, locationId)
      );
      const data = response.data?.data ?? response.data?.Data;
      if (response.data?.status && Array.isArray(data)) {
        return data;
      }
      return [];
    } catch (err) {
      console.error("Error fetching customer notifications:", err);
      throw err;
    }
  },

  /**
   * Get unread in-app notification count for logged-in customer.
   */
  async getCustomerUnreadNotificationCount(restaurantSlug, locationId = null) {
    try {
      if (!restaurantSlug) {
        throw new Error("Restaurant slug is required");
      }
      const response = await customerAxios.get(
        APIs.GET_CUSTOMER_NOTIFICATIONS_UNREAD_COUNT(restaurantSlug, locationId)
      );
      const data = response.data?.data ?? response.data?.Data;
      if (response.data?.status) {
        return Number(data?.unreadCount ?? data?.UnreadCount ?? 0) || 0;
      }
      return 0;
    } catch (err) {
      console.error("Error fetching unread notification count:", err);
      return 0;
    }
  },

  /**
   * Mark one customer notification as read.
   */
  async markCustomerNotificationRead(restaurantSlug, id, locationId = null) {
    try {
      if (!restaurantSlug || !id) {
        throw new Error("Restaurant slug and notification id are required");
      }
      const response = await customerAxios.patch(
        APIs.PATCH_CUSTOMER_NOTIFICATION_READ(restaurantSlug, id, locationId)
      );
      return !!response.data?.status;
    } catch (err) {
      console.error("Error marking customer notification as read:", err);
      return false;
    }
  },

  /**
   * Mark all customer notifications as read.
   */
  async markAllCustomerNotificationsRead(restaurantSlug, locationId = null) {
    try {
      if (!restaurantSlug) {
        throw new Error("Restaurant slug is required");
      }
      const response = await customerAxios.patch(
        APIs.PATCH_CUSTOMER_NOTIFICATIONS_READ_ALL(restaurantSlug, locationId)
      );
      const data = response.data?.data ?? response.data?.Data;
      if (response.data?.status) {
        return Number(data?.updatedCount ?? data?.UpdatedCount ?? 0) || 0;
      }
      return 0;
    } catch (err) {
      console.error("Error marking all customer notifications as read:", err);
      return 0;
    }
  },

  /**
   * Submit an online order (public - no authentication required)
   * @param {string} restaurantSlug - Restaurant slug from URL
   * @param {Object} orderData - Order data object
   * @param {string} orderData.customerName - Customer full name
   * @param {string} orderData.customerPhone - Customer phone number
   * @param {string} orderData.customerAddress - Delivery address
   * @param {string} orderData.postalCode - Postal code
   * @param {string} orderData.orderType - Order type (delivery, pickup, dine-in)
   * @param {number} orderData.totalAmount - Total order amount
   * @param {Array} orderData.cartItems - Array of cart items
   * @param {string} orderData.locationName - Selected location name
   * @param {string} orderData.orderComments - Additional order comments
   * @returns {Promise<Object>} Order response from API
   */
  async buildOrderPayload(restaurantSlug, orderData, options = {}) {
    console.log("=== onlineOrderingService.buildOrderPayload called ===");
    console.log("restaurantSlug:", restaurantSlug);
    console.log("orderData:", orderData);
    const awaitPayment = options?.awaitPayment === true;
    
    try {
      if (!restaurantSlug) {
        throw new Error("Restaurant slug is required");
      }

      const {
        customerName,
        customerPhone,
        customerAddress,
        postalCode,
        aptNo,
        deliveryInstructions,
        orderType,
        totalAmount,
        cartItems,
        locationName,
        orderComments,
        pickupDate,
        pickupTime,
        serviceAreaId,
        serviceAreaName,
        tableId,
        tableName,
        numberOfSeats,
        seatNo,
        locationId,
        tip = 0,
        vat = 0,
        voucherDiscount = 0,
        voucherCode = null,
        rewardId = null,
        rewardDiscount = 0,
        orderCountDiscountId = 0,
        orderCountDiscount = 0,
        serviceCharges = 0,
        deliveryCharges = 0,
        vatType = "inclusive",
        vatPercentage = 0,
        serviceChargesPercentage = 0,
        accountCustomerPhone = "",
        accountCustomerGuid = "",
        receivedAmount = null
      } = orderData;
      const normalizedTotalAmount = parseFloat(totalAmount) || 0;
      // We don't have detailed payment method integration yet, so record received card amount
      // as the same amount as order total unless frontend sends an explicit received amount.
      const normalizedReceivedAmount = receivedAmount !== null && receivedAmount !== undefined
        ? (parseFloat(receivedAmount) || normalizedTotalAmount)
        : normalizedTotalAmount;


      // Name and phone are only required for delivery orders
      const isDeliveryOrder = orderType?.toLowerCase() === "delivery";
      if (isDeliveryOrder && (!customerName || !customerPhone)) {
        throw new Error("Customer name and phone are required for delivery orders");
      }

      if (!cartItems || cartItems.length === 0) {
        throw new Error("Cart cannot be empty");
      }

      // Build order details from cart items (camelCase to match RecvOrder entity)
      // Main items and modifiers are SEPARATE entries linked by modifierRelationID
      const orderDetails = [];
      
      cartItems.forEach((item, itemIndex) => {
        const toInt = (v) => {
          if (v === null || v === undefined) return 0;
          const n = typeof v === "number" ? v : parseInt(v, 10);
          return Number.isFinite(n) ? n : 0;
        };

        // ---------------- Deal / Combo expansion ----------------
        // Deals are stored in cart as a single item with `dealSlots`.
        // For backoffice order view, we must also persist the selected deal items (+ their modifiers/variants)
        // as separate order detail rows, linked by ProductKey / ModifierRelationID.
        // When an order has more than one deal, ProductKey groups ALL sub-items of each deal (header + DEAL_ITEM + VARIANT + MODIFIER)
        // so they can be displayed together. Format: DEAL_{dealId}_{timestamp}_{dealIndexInCart}_{quantityIndex}.
        if (item?.isDeal && Array.isArray(item?.dealSlots) && item.dealSlots.length > 0) {
          const dealId = toInt(
            item.productId ||
              item.originalItem?.dealId ||
              item.originalItem?.DealID ||
              item.originalItem?.dealID ||
              (typeof item.id === "number" ? item.id : null) ||
              0
          );
          const dealCatalogMenuItemId = toInt(
            item.originalItem?.menuItem_ID ||
              item.originalItem?.MenuItem_ID ||
              item.originalItem?.menuItemId ||
              item.originalItem?.MenuItemId ||
              0
          );
          if (!(dealId > 0)) {
            throw new Error(
              `Deal "${item.name || "Unknown"}" is missing a valid deal ID. Please remove it from the cart and add it again.`
            );
          }
          const dealKey = `DEAL_${dealId || "X"}_${Date.now()}_${itemIndex + 1}`;
          const dealRelationBase = `deal-${dealId || "x"}-${Date.now()}-${itemIndex + 1}`;
          const dealOriginal = item.originalItem || {};
          const dealFixedPrice = parseFloat(dealOriginal.dealPrice ?? dealOriginal.DealPrice ?? 0) || 0;

          const isDineIn = orderType?.toLowerCase() === "dine-in" || orderType?.toLowerCase() === "dinein";
          const itemSeatNo = isDineIn ? normalizeSeatNo(item.seatNo ?? seatNo) : null;
          const itemSeatName = seatDisplayName(itemSeatNo);

          const dealBasePrice = parseFloat(item.price) || 0;
          const dealQty = parseFloat(item.quantity) || 1;

          // Collect deal child items first so we can apply pricing rules (Free/HalfPrice/Discount)
          const dealChildren = [];

          item.dealSlots.forEach((slotSel, slotIdx) => {
            const selectedItems = getRealDealSlotSelectedItems(slotSel, dealOriginal);
            (Array.isArray(selectedItems) ? selectedItems : []).forEach((si, siIdx) => {
              // Check if this is an auto-duplicated item
              const isAutoDuplicated = si.isAutoDuplicated === true;
              const childMenuItemId = toInt(
                si.itemId ||
                  si.item?.itemId ||
                  si.MenuItem_ID ||
                  si.item?.MenuItem_ID ||
                  si.menuItemId ||
                  si.item?.menuItem_ID ||
                  si.menuItem_ID ||
                  // Only use bare `id` when it is numeric (cart unique keys are strings like cart_item_...)
                  (typeof si.id === "number" ? si.id : null) ||
                  (typeof si.item?.id === "number" ? si.item.id : null) ||
                  0
              );
              
              // Log for debugging auto-duplication
              if (isAutoDuplicated) {
                console.log(`[Deal Order] Processing auto-duplicated item: ${si.itemName || si.name || 'Unknown'} (itemId: ${childMenuItemId}, index: ${siIdx})`);
              }

              if (!(childMenuItemId > 0)) {
                console.error(
                  `[Deal Order] Skipping deal child with invalid MenuItem_ID:`,
                  si?.itemName || si?.name || si?.item?.itemName || "Unknown",
                  si
                );
                return;
              }
              
              let childName =
                si.itemName ||
                si.ItemName ||
                si.name ||
                si.MenuItem_Name ||
                si.menuItem_Name ||
                si.item?.itemName ||
                si.item?.name ||
                `Item ${childMenuItemId || siIdx + 1}`;
              
              // Extract variant from separate fields first, then from name if needed
              let selectedVariant = (si.selectedVariant || si.variant || si.variantName || "").toString().trim();
              // Get variant ID - check multiple possible field names
              let selectedVariantId = toInt(
                si.selectedVariantId || 
                si.variantId || 
                si.variant_ID ||
                si.VariantId ||
                0
              );
              
              // If variant not in separate fields, try extracting from name (format: "Item Name (Variant)")
              // IMPORTANT: Keep variant in name for display (like regular items: "Pizza Margherita (Large)")
              if (!selectedVariant || selectedVariant === "") {
                const variantMatch = childName.match(/^(.+?)\s*\(([^)]+)\)$/);
                if (variantMatch && variantMatch[2] && variantMatch[2].trim()) {
                  // Extract variant text but keep it in the name
                  selectedVariant = variantMatch[2].trim();
                  console.log(`[Deal] Extracted variant "${selectedVariant}" from name "${childName}"`);
                  // childName already includes variant in parentheses, so keep it as is
                  // This matches how regular items work: variant stays in the name
                }
              } else {
                // If variant is in separate field, include it in the name if not already there
                if (!/\([^)]+\)$/.test(childName)) {
                  childName = `${childName} (${selectedVariant})`;
                  console.log(`[Deal] Added variant "${selectedVariant}" to name: "${childName}"`);
                } else {
                  console.log(`[Deal] Variant "${selectedVariant}" already in name: "${childName}"`);
                }
              }

              const payloadChildName = selectedVariant && selectedVariant.trim() !== ""
                ? childName.replace(/\s*\([^)]+\)\s*$/, "").trim()
                : childName;

              const baseChildPrice = parseFloat(si.itemPrice ?? si.price ?? 0) || 0;
              const childQty = parseFloat(si.quantity ?? 1) || 1;

              // Prepare modifiers list
              const childMods = Array.isArray(si.modifiers) ? si.modifiers : [];
              
              const dealChildModifierGroups =
                si.item?.modifierGroups ||
                si.modifierGroups ||
                si.originalItem?.modifierGroups ||
                [];
              const mappedMods = childMods.map((m) => {
                const modPrice = parseFloat(m.price) || 0;
                const modQty = parseFloat(m.quantity) || 1;
                let modMenuItemId = toInt(
                  m.originalModifier?.MenuItem_ID ||
                  m.originalModifier?.menuItem_ID ||
                  m.originalModifier?.modifierId ||
                  m.originalModifier?.modifierID ||
                  m.MenuItem_ID ||
                  m.menuItem_ID ||
                  m.modifierId ||
                  m.modifierID ||
                  0
                );
                if (!(modMenuItemId > 0)) {
                  modMenuItemId = lookupModifierMenuItemIdFromCartLine(
                    {
                      modifierGroups: dealChildModifierGroups,
                    },
                    m.name
                  );
                }
                const groupInfo = resolveModifierGroupInfo(m, {
                  modifierGroups: dealChildModifierGroups,
                });
                
                return {
                  modMenuItemId,
                  name: m.name || "",
                  price: modPrice, // Modifiers always keep full price
                  qty: modQty,
                  ModifierGroupID: groupInfo.ModifierGroupID,
                  ModifierGroupName: groupInfo.ModifierGroupName,
                };
              }).filter((m) => {
                if (!(m.modMenuItemId > 0)) {
                  console.warn(
                    `[Deal Order] Dropping modifier "${m.name}" with invalid MenuItem_ID for deal child ${childMenuItemId}`
                  );
                  return false;
                }
                return true;
              });
              const modifierTotal = mappedMods.reduce((sum, m) => sum + (m.price * (m.qty || 1)), 0);

              // Store which slot this item belongs to for slot-level pricing
              const slotId = slotSel.slotId || slotSel.slotID || slotSel.SlotId || slotSel.SlotID;
              const parsedSlotId = slotId != null ? toInt(slotId) : 0;
              // Prefer cart slot fields, fall back to deal definition (DealSlotSelector may omit customPrice)
              const resolvedSlot = resolveDealSlotPricing(slotSel, dealOriginal);
              // Build a per-child relation suffix so each child + its modifiers
              // get a UNIQUE ModifierRelationID. Without this, multiple children
              // in the same slot would share a relation id and the order details
              // view would attach all modifiers to the first matching child.
              const childPositionSuffix = `${slotIdx + 1}_${siIdx + 1}`;
              const childRelationId = `${dealRelationBase}_slot${childPositionSuffix}`;
              
              dealChildren.push({
                childMenuItemId,
                childName,
                payloadChildName,
                childDetail: si.itemDetail || si.detail || "",
                selectedVariant,
                selectedVariantId,
                baseChildPrice,
                childQty,
                childRelationId,
                // Use unique slot+item index so each child has a distinct relation id
                slotRelationIndex: childPositionSuffix,
                mappedMods,
                modifierTotal,
                slotId, // Store slot ID for slot-level pricing
                slotPricingType: resolvedSlot.pricingType || "",
                slotIsFree: resolvedSlot.isFree || false,
                slotDiscountPercentage: resolvedSlot.discountPercentage ?? 0,
                slotCustomPrice: resolvedSlot.customPrice ?? null,
                slotBuyQuantity: resolvedSlot.buyQuantity ?? 0,
                slotAutoDuplicateItem: resolvedSlot.autoDuplicateItem === true,
                isAutoDuplicated,
                si, // Keep selected item for ReportCat_ID / MenuGroupID attribution
              });
            });
          });

          // Apply deal pricing rules ONLY for dynamic deals (dealPrice = 0).
          // For fixed deal price (dealFixedPrice > 0) we keep header price and set child base prices to 0 to avoid double counting.
          const dynamicDeal = !(dealFixedPrice > 0);
          const minimumOrderValue = parseFloat(dealOriginal.minimumOrderValue ?? dealOriginal.MinimumOrderValue ?? 0) || 0;

          // Compute final base price per child item.
          // IMPORTANT: Modifiers ALWAYS keep their full price.
          // Deal pricing (Free / HalfPrice / Discount / CustomDiscount / FixedPricePerItem) applies
          // ONLY to the product/item price, never to modifiers.
          const childrenWithFinal = dealChildren.map((c) => {
            const finalMappedMods = c.mappedMods.map((m) => ({ ...m, finalPrice: m.price }));
            return {
              ...c,
              finalBasePrice: c.baseChildPrice,
              finalMappedMods,
            };
          });
          
          // Same gate as cartSlice / DealSummary / SideCart — includes FixedPricePerItem & legacy customPrice
          const useSlotLevelPricing =
            minimumOrderValue > 0 || dealHasSlotLevelPricing(dealOriginal, item.dealSlots);

          // FixedPricePerSlot: one amount for the whole slot (added to header payable once)
          let slotFixedPriceExtras = 0;
          
          if (dynamicDeal) {
            // Always use slot-level pricing (pricing config moved to slot level)
            if (useSlotLevelPricing) {
              // Group children by slotId for BOGO-within-slot logic
              const bySlot = {};
              childrenWithFinal.forEach((c) => {
                const sid = c.slotId ?? "unknown";
                if (!bySlot[sid]) bySlot[sid] = [];
                bySlot[sid].push(c);
              });

              Object.values(bySlot).forEach((slotChildren) => {
                const first = slotChildren[0];
                const slotPricingType = (first?.slotPricingType || "").trim();
                const isSlotFree = first?.slotIsFree || false;
                const slotDiscountPercentage = first?.slotDiscountPercentage || 0;
                const slotCustomPrice = first?.slotCustomPrice ?? null;
                const slotBuyQty = first?.slotBuyQuantity ?? 0;
                const slotAutoDup = first?.slotAutoDuplicateItem === true;
                const perItemCustom = getPerItemCustomPrice(slotPricingType, slotCustomPrice);
                const hasSlotDiscount =
                  slotPricingType === "Free" ||
                  isSlotFree ||
                  slotPricingType === "HalfPrice" ||
                  ((slotPricingType === "Discount" || slotPricingType === "CustomDiscount") &&
                    slotDiscountPercentage > 0);

                // One fixed price for the whole slot; item bases are zeroed (modifiers still charged)
                if (isFixedPricePerSlotType(slotPricingType) && parseSlotCustomPrice(slotCustomPrice) != null) {
                  slotFixedPriceExtras += parseSlotCustomPrice(slotCustomPrice);
                  slotChildren.forEach((c) => {
                    c.finalBasePrice = 0;
                  });
                  return;
                }

                if (slotAutoDup && (slotPricingType === "Free" || isSlotFree)) {
                  // Auto-duplicate BOGO: charge the chosen item, zero only the generated copy.
                  slotChildren.forEach((c) => {
                    c.finalBasePrice = chargesFreeSlotItem({
                      autoDuplicateItem: true,
                      buyQuantity: slotBuyQty,
                      isAutoDuplicated: c.isAutoDuplicated === true,
                    })
                      ? c.baseChildPrice
                      : 0;
                  });
                } else if (slotBuyQty > 0 && hasSlotDiscount && slotChildren.length > slotBuyQty) {
                  // BOGO within slot: sort by price (cheapest first), first slotBuyQty at full price, rest get slot pricing
                  const sorted = [...slotChildren].sort((a, b) => (a.baseChildPrice + a.modifierTotal) - (b.baseChildPrice + b.modifierTotal));
                  const discountedCount = slotChildren.length - slotBuyQty;
                  sorted.forEach((c, idx) => {
                    if (idx < discountedCount) {
                      if (slotPricingType === "Free" || isSlotFree) c.finalBasePrice = 0;
                      else if (slotPricingType === "HalfPrice") c.finalBasePrice = c.baseChildPrice * 0.5;
                      else if ((slotPricingType === "Discount" || slotPricingType === "CustomDiscount") && slotDiscountPercentage > 0) c.finalBasePrice = c.baseChildPrice - ((c.baseChildPrice * slotDiscountPercentage) / 100);
                    }
                    // else: keep full price (finalBasePrice = baseChildPrice, already set)
                  });
                } else if (isFixedPricePerItemType(slotPricingType, slotCustomPrice) && perItemCustom != null) {
                  // e.g. "Burger £5 Each" — every pick charged at customPrice (matches cart UI)
                  slotChildren.forEach((c) => {
                    c.finalBasePrice = perItemCustom;
                  });
                } else {
                  // Apply uniform slot-level pricing to all items in this slot
                  slotChildren.forEach((c) => {
                    if (slotPricingType === "Free" || isSlotFree) c.finalBasePrice = 0;
                    else if (
                      (slotPricingType === "CustomPrice" || slotPricingType === "FixedPricePerItem") &&
                      slotCustomPrice != null &&
                      !isNaN(parseFloat(slotCustomPrice))
                    ) {
                      c.finalBasePrice = parseFloat(slotCustomPrice);
                    }
                    else if (slotPricingType === "HalfPrice") c.finalBasePrice = c.baseChildPrice * 0.5;
                    else if ((slotPricingType === "Discount" || slotPricingType === "CustomDiscount") && slotDiscountPercentage > 0) c.finalBasePrice = c.baseChildPrice - ((c.baseChildPrice * slotDiscountPercentage) / 100);
                    // else: Fixed / NoDiscount — keep catalog price unless legacy customPrice applies below
                  });
                }

                // Legacy NoDiscount/Fixed + customPrice → treat as £X each (same as cartSlice)
                slotChildren.forEach((c) => {
                  c.finalBasePrice = applyPerItemCustomPriceIfNeeded(
                    c.finalBasePrice,
                    c.baseChildPrice,
                    slotPricingType,
                    slotCustomPrice
                  );
                });
              });
            } else {
              // Deal-level pricing when minimumOrderValue is NOT set (no slot-level pricing)
              // Apply same logic as cartSlice/DealSummary so order totals match UI
              const dealPricingType = (dealOriginal.dealPricingType ?? dealOriginal.DealPricingType ?? "").toString().trim().toLowerCase();
              const dealDiscountPercentage = parseFloat(dealOriginal.dealDiscountPercentage ?? dealOriginal.DealDiscountPercentage ?? 0) || 0;
              const buyQuantity = parseInt(dealOriginal.buyQuantity ?? dealOriginal.BuyQuantity ?? 1, 10) || 1;

              const totalChildren = childrenWithFinal.length;
              const discountedItemCount = (dealPricingType === "free" || dealPricingType === "halfprice") && buyQuantity > 0
                ? Math.max(0, totalChildren - buyQuantity)
                : totalChildren;

              // Sort by total price (item + modifiers) for BOGO so cheapest get discounted first
              const sortedChildren = (dealPricingType === "free" || dealPricingType === "halfprice")
                ? [...childrenWithFinal].sort((a, b) => (a.baseChildPrice + a.modifierTotal) - (b.baseChildPrice + b.modifierTotal))
                : childrenWithFinal;

              sortedChildren.forEach((c, sortedIndex) => {
                const getsDiscount = sortedIndex < discountedItemCount;
                // Modifiers ALWAYS keep full price; deal discounts apply to items only.
                if (dealPricingType === "free" && getsDiscount) {
                  c.finalBasePrice = 0;
                } else if (dealPricingType === "halfprice" && getsDiscount) {
                  c.finalBasePrice = c.baseChildPrice * 0.5;
                } else if ((dealPricingType === "customdiscount" || dealPricingType === "discount") && dealDiscountPercentage > 0) {
                  c.finalBasePrice = c.baseChildPrice - ((c.baseChildPrice * dealDiscountPercentage) / 100);
                }
                // else: keep full price (finalBasePrice already = baseChildPrice)
              });
            }
          } else {
            childrenWithFinal.forEach((c) => { c.finalBasePrice = 0; });
          }

          // One deal block with MenuItem_Qty = quantity (do not expand into separate instances).
          // Backoffice already multiplies child/modifier display qty by parent deal qty.
          const dealQtyNum = Math.max(1, Math.floor(Number(dealQty)) || 1);
          {
            const instanceDealKey = dealKey;
            // Modifier surcharges charged inside this deal instance. Deal pricing never
            // discounts modifiers, so these are their full prices.
            const dealInstanceModifierTotal = childrenWithFinal.reduce((sum, c) => {
              const mods = c.finalMappedMods || c.mappedMods || [];
              return sum + mods.reduce((modSum, m) => {
                const unitPrice = m.finalPrice !== undefined ? m.finalPrice : m.price;
                return modSum + (unitPrice || 0) * (m.qty || 1);
              }, 0);
            }, 0);

            // POS convention (matches the Flutter till): the deal HEADER carries the whole
            // billable package — discounted item bases plus modifier surcharges — while every
            // child line stays at SubTotal 0. Anything that sums line SubTotals (backend
            // VAT/service, reports, order views) then reads the package price exactly once.
            // Fixed-price: header = dealFixedPrice (children base prices are zeroed above).
            // Dynamic/BOGO / £X each: header = sum of discounted child base prices (+ FixedPricePerSlot extras).
            const dealInstancePayableTotal =
              (dynamicDeal ? 0 : dealFixedPrice) +
              (dynamicDeal ? slotFixedPriceExtras : 0) +
              childrenWithFinal.reduce((sum, c) => {
                const baseTotal = (c.finalBasePrice || 0) * (c.childQty || 1);
                return sum + baseTotal;
              }, 0) +
              dealInstanceModifierTotal;

            // Deal header row — quantity on a single line
            const headerRelationId = dealRelationBase;
            const dealHeaderRecvorderDetailId = newRecvorderDetailGuid();
            orderDetails.push({
              RecvorderDetail_ID: dealHeaderRecvorderDetailId,
              Recvorder_InvoiceNo: "",
              MenuItem_ID: dealCatalogMenuItemId > 0 ? dealCatalogMenuItemId : dealId,
              MenuItem_Name: item.name || item.originalItem?.dealName || "Deal",
              MenuItem_Qty: dealQtyNum,
              // POS style: keep payable unit amount on deal header line
              MenuItem_Price: dealInstancePayableTotal,
              MenuItem_Detail: item.description || item.originalItem?.dealDescription || "",
              Recvorder_SInstructions: item.specialInstructions || "",
              MenuItem_Isupdated: false,
              MenuItem_UpdatedValue: 0,
              MenuItem_Status: "open",
              MenuItem_UpdatedDone: false,
              MenuItem_HaveModifier: false,
              MenuItem_IsModifier: false,
              Parent_MenuItemID: 0,
              ModifierRelationID: headerRelationId,
              CompID: 0,
              ReportCat_ID: 0,
              MenuItem_CostPrice: 0,
              Retail_Tax: 0,
              NetProfit: 0,
              SubTotal: dealInstancePayableTotal * dealQtyNum,
              MenuItem_Type: "COMBO_DEAL",
              IsItemSubmitted: false,
              IsItemVoid: false,
              SeatNo: itemSeatNo,
              SeatName: itemSeatName,
              IsPrinted: false,
              DiscountAmount: 0,
              DiscountInPercentage: 0,
              ProductKey: instanceDealKey,
              MenuID: 0,
              MenuGroupID: 0,
              DealID: dealId,
              DealSlotID: null,
              UserAllergens: ""
            });

            // Emit deal child products + their modifiers/variants once (parent qty scales display)
            childrenWithFinal.forEach((c) => {
              const instanceBaseRelationId = dealRelationBase;
              const instanceChildRelationId = `${instanceBaseRelationId}_slot${c.slotRelationIndex || 1}`;
              const dealSlotId = c.slotId != null ? toInt(c.slotId) : null;
              const childUnitOriginalPrice = c.baseChildPrice || 0;
              const childUnitFinalPrice = c.finalBasePrice || 0;
              // Child lines are included in the deal header payable amount, so SubTotal stays 0.
              // The deal-applied price is still recoverable per item as
              // MenuItem_Price - DiscountAmount / MenuItem_Qty, which is how order views and
              // the refund calculator read it (e.g. £7.00 catalog, £3.50 discount => £3.50 paid).
              const childUnitDiscount = Math.max(0, childUnitOriginalPrice - childUnitFinalPrice);
              const childDiscountAmount = childUnitDiscount * (c.childQty || 1);
              const childDiscountPercentage = childUnitOriginalPrice > 0
                ? (childUnitDiscount / childUnitOriginalPrice) * 100
                : 0;
              const dealChildRecvorderDetailId = newRecvorderDetailGuid();
              const dealChildReport = pickReportFields(c.si?.originalItem, c.si?.item, c.si);
              orderDetails.push({
                RecvorderDetail_ID: dealChildRecvorderDetailId,
                RecvorderDetail_ParentID: dealHeaderRecvorderDetailId,
                Recvorder_InvoiceNo: "",
                MenuItem_ID: c.childMenuItemId,
                MenuItem_Name: c.payloadChildName || c.childName,
                MenuItem_Qty: c.childQty,
                // Catalog price of the slot pick; payable amount stays on the header.
                MenuItem_Price: childUnitOriginalPrice,
                MenuItem_Detail: c.childDetail,
                Recvorder_SInstructions: "",
                MenuItem_Isupdated: false,
                MenuItem_UpdatedValue: 0,
                MenuItem_Status: "open",
                MenuItem_UpdatedDone: false,
                MenuItem_HaveModifier: c.mappedMods.length > 0,
                MenuItem_IsModifier: false,
                Parent_MenuItemID: 0,
                ModifierRelationID: instanceChildRelationId,
                CompID: 0,
                ReportCat_ID: dealChildReport.ReportCat_ID,
                MenuItem_CostPrice: dealChildReport.MenuItem_CostPrice,
                Retail_Tax: dealChildReport.Retail_Tax,
                NetProfit: 0,
                SubTotal: 0,
                MenuItem_Type: "DEAL_ITEM",
                IsItemSubmitted: false,
                IsItemVoid: false,
                SeatNo: itemSeatNo,
                SeatName: itemSeatName,
                IsPrinted: false,
                DiscountAmount: childDiscountAmount,
                DiscountInPercentage: childDiscountPercentage,
                ProductKey: instanceDealKey,
                MenuID: dealChildReport.MenuID,
                MenuGroupID: dealChildReport.MenuGroupID,
                DealID: dealId,
                DealSlotID: dealSlotId,
                UserAllergens: ""
              });

              // Variant row (optional, purely for display like normal orders)
              if (c.selectedVariant && c.selectedVariant.trim() !== "") {
                const variantMenuItemId = (c.selectedVariantId && c.selectedVariantId > 0) 
                  ? c.selectedVariantId 
                  : c.childMenuItemId;
                
                if (!c.selectedVariantId || c.selectedVariantId === 0) {
                  console.warn(`[Deal] Variant ID not found for "${c.selectedVariant}", using parent item ID ${c.childMenuItemId} as fallback`);
                } else {
                  console.log(`[Deal] Creating VARIANT row for "${c.selectedVariant}" (ID: ${c.selectedVariantId}) linked to item ID ${c.childMenuItemId} (${c.childName})`);
                }
                
                orderDetails.push({
                  RecvorderDetail_ID: newRecvorderDetailGuid(),
                  RecvorderDetail_ParentID: dealChildRecvorderDetailId,
                  Recvorder_InvoiceNo: "",
                  MenuItem_ID: variantMenuItemId,
                  MenuItem_Name: c.selectedVariant.trim(),
                  MenuItem_Qty: 1,
                  // Variant row is display-only; amount stays on deal header / parent item.
                  MenuItem_Price: 0,
                  MenuItem_Detail: "",
                  Recvorder_SInstructions: "",
                  MenuItem_Isupdated: false,
                  MenuItem_UpdatedValue: 0,
                  MenuItem_Status: "open",
                  MenuItem_UpdatedDone: false,
                  MenuItem_HaveModifier: false,
                  MenuItem_IsModifier: false,
                  Parent_MenuItemID: c.childMenuItemId,
                  ModifierRelationID: instanceChildRelationId,
                  CompID: 0,
                  ReportCat_ID: 0,
                  MenuItem_CostPrice: 0,
                  Retail_Tax: 0,
                  NetProfit: 0,
                  SubTotal: 0,
                  MenuItem_Type: "VARIANT",
                  IsItemSubmitted: false,
                  IsItemVoid: false,
                  SeatNo: itemSeatNo,
                  SeatName: itemSeatName,
                  IsPrinted: false,
                  DiscountAmount: childDiscountAmount,
                  DiscountInPercentage: childDiscountPercentage,
                  ProductKey: instanceDealKey,
                  MenuID: 0,
                  MenuGroupID: 0,
                  DealID: dealId,
                  DealSlotID: dealSlotId,
                  UserAllergens: ""
                });
              }

              // Modifiers for this deal item
              const modifiersToUse = c.finalMappedMods || c.mappedMods;
              modifiersToUse.forEach((m) => {
                const modifierFinalPrice = m.finalPrice !== undefined ? m.finalPrice : m.price;
                const modifierOriginalPrice = m.price || 0;
                const modifierUnitDiscount = Math.max(0, modifierOriginalPrice - (modifierFinalPrice || 0));
                // Line-level, matching DEAL_ITEM rows, so readers apply one rule:
                // charged amount = MenuItem_Price * MenuItem_Qty - DiscountAmount.
                const modifierDiscountAmount = modifierUnitDiscount * (m.qty || 1);
                const modifierDiscountPercentage = modifierOriginalPrice > 0
                  ? (modifierUnitDiscount / modifierOriginalPrice) * 100
                  : 0;
                orderDetails.push({
                  RecvorderDetail_ID: newRecvorderDetailGuid(),
                  RecvorderDetail_ParentID: dealChildRecvorderDetailId,
                  Recvorder_InvoiceNo: "",
                  MenuItem_ID: m.modMenuItemId,
                  MenuItem_Name: m.name || "",
                  MenuItem_Qty: m.qty,
                  // Save original sale price on line, discount fields indicate applied deal discount
                  MenuItem_Price: modifierOriginalPrice,
                  MenuItem_Detail: "",
                  Recvorder_SInstructions: "",
                  MenuItem_Isupdated: false,
                  MenuItem_UpdatedValue: 0,
                  MenuItem_Status: "open",
                  MenuItem_UpdatedDone: false,
                  MenuItem_HaveModifier: false,
                  MenuItem_IsModifier: true,
                  Parent_MenuItemID: c.childMenuItemId,
                  ModifierRelationID: instanceChildRelationId,
                  CompID: 0,
                  ReportCat_ID: 0,
                  MenuItem_CostPrice: 0,
                  Retail_Tax: 0,
                  NetProfit: 0,
                  // Deal modifier surcharges are already inside the header package price,
                  // so this line carries no money. Its charge stays readable as
                  // MenuItem_Price - DiscountAmount for display.
                  SubTotal: 0,
                  MenuItem_Type: "MODIFIER",
                  IsItemSubmitted: false,
                  IsItemVoid: false,
                  SeatNo: itemSeatNo,
                  SeatName: itemSeatName,
                  IsPrinted: false,
                  DiscountAmount: modifierDiscountAmount,
                  DiscountInPercentage: modifierDiscountPercentage,
                  ProductKey: instanceDealKey,
                  MenuID: 0,
                  MenuGroupID: 0,
                  DealID: dealId,
                  DealSlotID: dealSlotId,
                  UserAllergens: "",
                  ModifierGroupID: m.ModifierGroupID ?? null,
                  ModifierGroupName: m.ModifierGroupName ?? null,
                });
              });
            });
          }

          // Skip the normal item processing for deals
          return;
        }

        // ---------------- Combo expansion (itemType: "Combo" with dealProducts) ----------------
        // Combos from the "Deals" page use dealProducts instead of dealSlots
        const isCombo = (item?.itemType === "Combo" || item?.itemType === "combo") && 
                        Array.isArray(item?.dealProducts) && item.dealProducts.length > 0;
        
        if (isCombo) {
          const comboId = toInt(item.productId || item.id || item.originalItem?.MenuItem_ID || item.originalItem?.id || 0);
          const comboKey = `COMBO_${comboId || "X"}_${Date.now()}_${itemIndex + 1}`;

          const isDineIn = orderType?.toLowerCase() === "dine-in" || orderType?.toLowerCase() === "dinein";
          const itemSeatNo = isDineIn ? normalizeSeatNo(item.seatNo ?? seatNo) : null;
          const itemSeatName = seatDisplayName(itemSeatNo);

          const comboBasePrice = parseFloat(item.price) || 0;
          const comboQty = parseFloat(item.quantity) || 1;

          // Combo header row (combos are menu items; carry ReportCat_ID for category reports)
          const comboHeaderRecvorderDetailId = newRecvorderDetailGuid();
          const comboHeaderRelationId = `${comboHeaderRecvorderDetailId}_${Date.now()}`;
          const comboReport = pickReportFields(item.originalItem, item);
          orderDetails.push({
            RecvorderDetail_ID: comboHeaderRecvorderDetailId,
            Recvorder_InvoiceNo: "",
            MenuItem_ID: comboId,
            MenuItem_Name: item.name || item.originalItem?.MenuItem_Name || "Combo",
            MenuItem_Qty: comboQty,
            MenuItem_Price: comboBasePrice,
            MenuItem_Detail: item.description || item.originalItem?.MenuItem_Detail || "",
            Recvorder_SInstructions: item.specialInstructions || "",
            MenuItem_Isupdated: false,
            MenuItem_UpdatedValue: 0,
            MenuItem_Status: "open",
            MenuItem_UpdatedDone: false,
            MenuItem_HaveModifier: false,
            MenuItem_IsModifier: false,
            Parent_MenuItemID: 0,
            ModifierRelationID: comboHeaderRelationId,
            CompID: 0,
            ReportCat_ID: comboReport.ReportCat_ID,
            MenuItem_CostPrice: comboReport.MenuItem_CostPrice,
            Retail_Tax: comboReport.Retail_Tax,
            NetProfit: 0,
            SubTotal: comboBasePrice * comboQty,
            MenuItem_Type: "COMBO",
            IsItemSubmitted: false,
            IsItemVoid: false,
            SeatNo: itemSeatNo,
            SeatName: itemSeatName,
            IsPrinted: false,
            DiscountAmount: 0,
            DiscountInPercentage: 0,
            ProductKey: comboKey,
            MenuID: comboReport.MenuID,
            MenuGroupID: comboReport.MenuGroupID,
            DealID: comboId,
            DealSlotID: null,
            UserAllergens: ""
          });

          // Emit combo child products
          item.dealProducts.forEach((product, prodIdx) => {
            // API returns productId, also check other common field names
            const childMenuItemId = toInt(product.productId || product.MenuItem_ID || product.menuItemId || product.id || 0);
            let childName = product.productName || product.MenuItem_Name || product.name || `Product ${prodIdx + 1}`;
            const payloadChildName = selectedVariant && selectedVariant.trim() !== ""
              ? childName.replace(/\s*\([^)]+\)\s*$/, "").trim()
              : childName;

            const childPrice = parseFloat(product.price || product.MenuItem_Price || 0) || 0;
            const childQty = parseFloat(product.quantity || 1) || 1;
            const childRelationId = `${comboKey}_${childMenuItemId || "X"}_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;

            // Debug: Log product structure to understand variant storage
            console.log(`[Combo] Processing product ${prodIdx + 1}:`, {
              name: childName,
              productId: childMenuItemId,
              productKeys: Object.keys(product)
            });

            // Get variant from product data (similar to deal items)
            // Check for variant in separate fields first, then extract from name if needed
            // Check all possible variant field names
            let selectedVariant = (
              product.selectedVariant || 
              product.variant || 
              product.variantName || 
              product.VariantName ||
              product.selectedVariantName ||
              product.variant_name ||
              ""
            ).toString().trim();
            let selectedVariantId = toInt(
              product.variantId || 
              product.selectedVariantId || 
              product.VariantId ||
              product.variant_ID ||
              0
            );

            // If variant not in separate fields, try extracting from name (format: "Item Name (Variant Name)")
            // IMPORTANT: Keep variant in name for display (like regular items: "Pizza Margherita (Large)")
            if (!selectedVariant || selectedVariant === "") {
              // Try to extract variant from name - but DON'T remove it from name
              // Match pattern: "Item Name (Variant)" at the end of the string
              const variantMatch = childName.match(/^(.+?)\s*\(([^)]+)\)$/);
              if (variantMatch && variantMatch[2] && variantMatch[2].trim()) {
                // Extract variant text but keep it in the name
                selectedVariant = variantMatch[2].trim();
                // childName already includes variant in parentheses, so keep it as is
                // This matches how regular items work: variant stays in the name
                console.log(`[Combo] Extracted variant "${selectedVariant}" from name "${childName}"`);
              }
            } else {
              // If variant is in separate field, include it in the name (like regular items and deal items)
              // This ensures the variant shows in the backoffice order view
              if (!/\([^)]+\)$/.test(childName)) {
                childName = `${childName} (${selectedVariant})`;
                console.log(`[Combo] Added variant "${selectedVariant}" to name: "${childName}"`);
              }
            }

            // Combos have a fixed price, so child items are included in combo price.
            // SubTotal AND MenuItem_Price payable contribution must stay 0 so VAT/service
            // engines that sum line amounts cannot double-count against the combo header.
            const comboChildRecvorderDetailId = newRecvorderDetailGuid();
            const comboChildReport = pickReportFields(product);
            orderDetails.push({
              RecvorderDetail_ID: comboChildRecvorderDetailId,
              RecvorderDetail_ParentID: comboHeaderRecvorderDetailId,
              Recvorder_InvoiceNo: "",
              MenuItem_ID: childMenuItemId,
              MenuItem_Name: payloadChildName || childName,
              MenuItem_Qty: childQty,
              MenuItem_Price: 0,
              MenuItem_Detail: product.MenuItem_Detail || product.detail || "",
              Recvorder_SInstructions: "",
              MenuItem_Isupdated: false,
              MenuItem_UpdatedValue: 0,
              MenuItem_Status: "open",
              MenuItem_UpdatedDone: false,
              MenuItem_HaveModifier: false,
              MenuItem_IsModifier: true, // COMBO ITEM is stored as IsModifier=true in POS structure
              Parent_MenuItemID: comboId || 0, // Links to combo
              ModifierRelationID: childRelationId,
              CompID: 0,
              ReportCat_ID: comboChildReport.ReportCat_ID,
              MenuItem_CostPrice: comboChildReport.MenuItem_CostPrice,
              Retail_Tax: comboChildReport.Retail_Tax,
              NetProfit: 0,
              SubTotal: 0, // Child items are included in combo price, so SubTotal = 0
              MenuItem_Type: "COMBO ITEM", // POS structure: combo header + COMBO ITEM lines
              IsItemSubmitted: false,
              IsItemVoid: false,
              SeatNo: itemSeatNo,
              SeatName: itemSeatName,
              IsPrinted: false,
              DiscountAmount: childPrice,
              DiscountInPercentage: childPrice > 0 ? 100 : 0,
              ProductKey: comboKey,
              MenuID: comboChildReport.MenuID,
              MenuGroupID: comboChildReport.MenuGroupID,
              DealID: comboId,
              DealSlotID: null,
              UserAllergens: ""
            });

            // Add variant row if variant exists (similar to deal items)
            if (selectedVariant && selectedVariant.trim() !== "") {
              // Use variant ID if available, otherwise use parent item ID as fallback
              // The backend requires MenuItem_ID > 0, so we can't use 0
              const variantMenuItemId = (selectedVariantId && selectedVariantId > 0) 
                ? selectedVariantId 
                : childMenuItemId; // Fallback to parent item ID if variant ID not available
              
              if (!selectedVariantId || selectedVariantId === 0) {
                console.warn(`[Combo] Variant ID not found for "${selectedVariant}", using parent item ID ${childMenuItemId} as fallback`);
              } else {
                console.log(`[Combo] Creating VARIANT row for "${selectedVariant}" (ID: ${selectedVariantId}) linked to item ID ${childMenuItemId}`);
              }
              
              orderDetails.push({
                RecvorderDetail_ID: newRecvorderDetailGuid(),
                RecvorderDetail_ParentID: comboChildRecvorderDetailId,
                Recvorder_InvoiceNo: "",
                MenuItem_ID: variantMenuItemId,
                MenuItem_Name: selectedVariant.trim(),
                MenuItem_Qty: 1,
                MenuItem_Price: 0,
                MenuItem_Detail: "",
                Recvorder_SInstructions: "",
                MenuItem_Isupdated: false,
                MenuItem_UpdatedValue: 0,
                MenuItem_Status: "open",
                MenuItem_UpdatedDone: false,
                MenuItem_HaveModifier: false,
                MenuItem_IsModifier: false,
                Parent_MenuItemID: childMenuItemId, // Links to the combo child item
                ModifierRelationID: childRelationId, // Same relation ID as the child item
                CompID: 0,
                ReportCat_ID: 0,
                MenuItem_CostPrice: 0,
                Retail_Tax: 0,
                NetProfit: 0,
                SubTotal: 0,
                MenuItem_Type: "VARIANT",
                IsItemSubmitted: false,
                IsItemVoid: false,
                SeatNo: itemSeatNo,
                SeatName: itemSeatName,
                IsPrinted: false,
                DiscountAmount: 0,
                DiscountInPercentage: 0,
                ProductKey: comboKey, // Same ProductKey as combo for grouping
                MenuID: 0,
                MenuGroupID: 0,
                DealID: comboId,
                DealSlotID: null,
                UserAllergens: ""
              });
            } else {
              console.log(`[Combo] No variant found for product "${childName}"`);
            }
          });

          // Skip the normal item processing for combos
          return;
        }

        const getFirstDefined = (...values) => values.find((v) => v !== undefined && v !== null && v !== "");

        // Parent catalog ID for the main PRODUCT line (never the variant row alone)
        let parentMenuItemId =
          item.originalItem?.MenuItem_ID ||
          item.originalItem?.menuItem_ID ||
          item.originalItem?.itemId ||
          item.productId ||
          item.id ||
          0;
        if (typeof parentMenuItemId === "string") {
          const parsed = parseInt(parentMenuItemId, 10);
          parentMenuItemId = isNaN(parsed) ? 0 : parsed;
        }
        if (typeof parentMenuItemId !== "number" || isNaN(parentMenuItemId)) {
          parentMenuItemId = 0;
        }

        let selectedVariantId = toInt(
          item.selectedVariantId ||
            item.variantId ||
            item.originalItem?.selectedVariantId ||
            0
        );
        // If selectedVariantId equals parent, treat as no separate variant row
        if (selectedVariantId > 0 && selectedVariantId === parentMenuItemId) {
          selectedVariantId = 0;
        }

        // Short variant name for VARIANT child line (not the full DefaultVariantTitle)
        let selectedVariantShort = (
          item.selectedVariant ||
          item.variant ||
          item.variantName ||
          ""
        )
          .toString()
          .trim();
        if (!selectedVariantShort && item.name) {
          const variantMatch = String(item.name).match(/\(([^)]+)\)\s*$/);
          if (variantMatch) selectedVariantShort = variantMatch[1].trim();
        }

        const menuItemId = parentMenuItemId;

        // Check if item has modifiers
        const hasModifiers = item.modifiers && item.modifiers.length > 0;
        const hasVariantChild = selectedVariantId > 0;

        // POS uses ModifierRelationID as the cart-line key. Always set a unique value
        // (even with no modifiers), or empty strings collapse multiple products into one line.
        // Format matches POS: {RecvorderDetail_ID}_{timestamp}
        const mainLineRecvorderDetailId = newRecvorderDetailGuid();
        const modifierRelationID = `${mainLineRecvorderDetailId}_${Date.now()}`;

        const basePrice = parseFloat(item.price) || 0;
        const quantity = parseFloat(item.quantity) || 1;

        // Add the MAIN menu item (property names match backend RecvOrderDetail entity)
        // Dine-in: only send a real seat (1+). Never send 0 — that shows as "Seat 0" in POS / emails.
        const isDineIn = orderType?.toLowerCase() === "dine-in" || orderType?.toLowerCase() === "dinein";
        const itemSeatNo = isDineIn ? normalizeSeatNo(item.seatNo ?? seatNo) : null;
        const itemSeatName = seatDisplayName(itemSeatNo);

        const rawProductKey = getFirstDefined(
          item.productKey,
          item.ProductKey,
          item.originalItem?.productKey,
          item.originalItem?.ProductKey,
          item.originalItem?.Product_Key,
          item.originalItem?.Productkey
        );
        const normalizedProductKey = rawProductKey !== undefined && rawProductKey !== null && String(rawProductKey).trim() !== ""
          ? `${String(rawProductKey).trim()}_${itemIndex + 1}`
          : `LINE_${itemIndex + 1}_${Date.now()}`;

        const rawDealId = getFirstDefined(
          item.dealId,
          item.DealID,
          item.dealID,
          item.originalItem?.dealId,
          item.originalItem?.DealID,
          item.originalItem?.dealID
        );
        const normalizedDealId = rawDealId !== undefined && rawDealId !== null && String(rawDealId).trim() !== ""
          ? toInt(rawDealId)
          : null;

        const rawDealSlotId = getFirstDefined(
          item.dealSlotId,
          item.DealSlotID,
          item.dealSlotID,
          item.originalItem?.dealSlotId,
          item.originalItem?.DealSlotID,
          item.originalItem?.dealSlotID
        );
        const normalizedDealSlotId = rawDealSlotId !== undefined && rawDealSlotId !== null && String(rawDealSlotId).trim() !== ""
          ? toInt(rawDealSlotId)
          : null;

        const parentDisplayName =
          item.originalItem?.MenuItem_Name ||
          item.originalItem?.menuItem_Name ||
          item.originalItem?.itemName ||
          item.originalItem?.name ||
          (() => {
            const n = (item.name || "").toString().trim();
            const m = n.match(/^(.+?)\s*\([^)]+\)\s*$/);
            return m ? m[1].trim() : n;
          })();

        // Prefer catalog (originalItem) over cart snapshot — cart can hold stale/cross-company ReportCat_ID.
        const productReport = pickReportFields(item.originalItem, item);
        orderDetails.push({
          RecvorderDetail_ID: mainLineRecvorderDetailId,
          Recvorder_InvoiceNo: "",
          MenuItem_ID: menuItemId,
          MenuItem_Name: parentDisplayName,
          MenuItem_Qty: quantity,
          MenuItem_Price: basePrice,
          MenuItem_Detail: item.originalItem?.MenuItem_Detail || item.description || "",
          Recvorder_SInstructions: item.specialInstructions || "",
          MenuItem_Isupdated: false,
          MenuItem_UpdatedValue: 0,
          MenuItem_Status: "open",
          MenuItem_UpdatedDone: false,
          MenuItem_HaveModifier: hasModifiers || hasVariantChild,
          MenuItem_IsModifier: false, // This is the MAIN item (keep false even when it has modifier children)
          Parent_MenuItemID: 0, // Always 0 for parent PRODUCT — reports must not treat this as a child line
          ModifierRelationID: modifierRelationID,
          CompID: 0,
          ReportCat_ID: productReport.ReportCat_ID,
          MenuItem_CostPrice: productReport.MenuItem_CostPrice,
          Retail_Tax: productReport.Retail_Tax,
          NetProfit: 0,
          SubTotal: basePrice * quantity,
          MenuItem_Type: "PRODUCT",
          IsItemSubmitted: false,
          IsItemVoid: false,
          SeatNo: itemSeatNo,
          SeatName: itemSeatName,
          IsPrinted: false,
          DiscountAmount: 0,
          DiscountInPercentage: 0,
          ProductKey: normalizedProductKey,
          MenuID: productReport.MenuID,
          MenuGroupID: productReport.MenuGroupID,
          DealID: normalizedDealId,
          DealSlotID: normalizedDealSlotId,
          UserAllergens: joinSelectedAllergenSolutions(item)
        });

        // Add VARIANT child so reports can resolve Parent (Variant) via DefaultVariantTitle
        if (hasVariantChild) {
          orderDetails.push({
            RecvorderDetail_ID: newRecvorderDetailGuid(),
            RecvorderDetail_ParentID: mainLineRecvorderDetailId,
            Recvorder_InvoiceNo: "",
            MenuItem_ID: selectedVariantId,
            MenuItem_Name: selectedVariantShort || String(selectedVariantId),
            // Match parent line qty — cart stores variant once per unit of the parent item
            MenuItem_Qty: quantity,
            MenuItem_Price: 0,
            MenuItem_Detail: "",
            Recvorder_SInstructions: "",
            MenuItem_Isupdated: false,
            MenuItem_UpdatedValue: 0,
            MenuItem_Status: "open",
            MenuItem_UpdatedDone: false,
            MenuItem_HaveModifier: false,
            MenuItem_IsModifier: false,
            Parent_MenuItemID: menuItemId,
            ModifierRelationID: modifierRelationID,
            CompID: 0,
            ReportCat_ID: 0,
            MenuItem_CostPrice: 0,
            Retail_Tax: 0,
            NetProfit: 0,
            SubTotal: 0,
            MenuItem_Type: "VARIANT",
            IsItemSubmitted: false,
            IsItemVoid: false,
            SeatNo: itemSeatNo,
            SeatName: itemSeatName,
            IsPrinted: false,
            DiscountAmount: 0,
            DiscountInPercentage: 0,
            ProductKey: normalizedProductKey,
            MenuID: 0,
            MenuGroupID: 0,
            DealID: normalizedDealId,
            DealSlotID: normalizedDealSlotId,
            UserAllergens: ""
          });
        }

        // Add each MODIFIER as a separate order detail entry
        if (hasModifiers) {
          item.modifiers.forEach((modifier) => {
            // Get modifier's MenuItem_ID (POS needs the modifier SKU, not the parent product id)
            let modifierMenuItemId =
              modifier.originalModifier?.MenuItem_ID ??
              modifier.originalModifier?.MenuItem_Id ??
              modifier.originalModifier?.menuItem_ID ??
              modifier.originalModifier?.menuItem_Id ??
              modifier.originalModifier?.modifierId ??
              modifier.originalModifier?.ModifierId ??
              modifier.originalModifier?.ModifierID ??
              modifier.MenuItem_ID ??
              modifier.menuItem_ID ??
              modifier.MenuItem_Id ??
              modifier.modifierId ??
              modifier.ModifierId ??
              modifier.id ??
              0;

            if (typeof modifierMenuItemId === "string") {
              const parsed = parseInt(modifierMenuItemId, 10);
              modifierMenuItemId = isNaN(parsed) ? 0 : parsed;
            }
            if (typeof modifierMenuItemId !== "number" || isNaN(modifierMenuItemId)) {
              modifierMenuItemId = 0;
            }
            if (!modifierMenuItemId) {
              modifierMenuItemId = lookupModifierMenuItemIdFromCartLine(item, modifier.name);
            }
            // Cart keeps modifier.quantity per unit of the parent item. Order lines must
            // scale by parent qty or the price-quote / submit undercharges toppings
            // (e.g. 3 pizzas + £5 mushroom → SubTotal £5 instead of £15).
            const unitModQty = parseFloat(modifier.quantity) || 1;
            const modQty = unitModQty * quantity;
            const catalogModPrice = parseFloat(modifier.price) || 0;
            const modPrice =
              modifier.chargePrice !== undefined && modifier.chargePrice !== null
                ? parseFloat(modifier.chargePrice) || 0
                : catalogModPrice;
 
            // Include seat number for modifiers in dine-in orders (use same seat as main item)
            const modifierSeatNo = itemSeatNo;
            const modifierSeatName = itemSeatName;

            const modRawDealId = getFirstDefined(
              modifier.dealId,
              modifier.DealID,
              modifier.originalModifier?.dealId,
              modifier.originalModifier?.DealID,
              normalizedDealId
            );
            const modDealId = modRawDealId !== undefined && modRawDealId !== null && String(modRawDealId).trim() !== ""
              ? toInt(modRawDealId)
              : null;

            const modRawDealSlotId = getFirstDefined(
              modifier.dealSlotId,
              modifier.DealSlotID,
              modifier.originalModifier?.dealSlotId,
              modifier.originalModifier?.DealSlotID,
              normalizedDealSlotId
            );
            const modDealSlotId = modRawDealSlotId !== undefined && modRawDealSlotId !== null && String(modRawDealSlotId).trim() !== ""
              ? toInt(modRawDealSlotId)
              : null;

            const modifierGroupInfo = resolveModifierGroupInfo(modifier, item);

            orderDetails.push({
              RecvorderDetail_ID: newRecvorderDetailGuid(),
              RecvorderDetail_ParentID: mainLineRecvorderDetailId,
              Recvorder_InvoiceNo: "",
              MenuItem_ID: modifierMenuItemId,
              MenuItem_Name: modifier.name || "",
              MenuItem_Qty: modQty,
              MenuItem_Price: modPrice,
              MenuItem_Detail: "",
              Recvorder_SInstructions: "",
              MenuItem_Isupdated: false,
              MenuItem_UpdatedValue: 0,
              MenuItem_Status: "open",
              MenuItem_UpdatedDone: false,
              MenuItem_HaveModifier: false,
              MenuItem_IsModifier: true, // This IS a modifier
              Parent_MenuItemID: menuItemId, // Links to parent item's menuItem_ID
              ModifierRelationID: modifierRelationID, // Same as parent to link them
              CompID: 0,
              ...(() => {
                const modReport = pickReportFields(modifier.originalModifier, modifier);
                return {
                  ReportCat_ID: modReport.ReportCat_ID,
                  MenuItem_CostPrice: modReport.MenuItem_CostPrice,
                  Retail_Tax: modReport.Retail_Tax,
                  MenuID: modReport.MenuID,
                  MenuGroupID: modReport.MenuGroupID,
                };
              })(),
              NetProfit: 0,
              SubTotal: modPrice * modQty,
              MenuItem_Type: "MODIFIER",
              IsItemSubmitted: false,
              IsItemVoid: false,
              SeatNo: modifierSeatNo,
              SeatName: modifierSeatName,
              IsPrinted: false,
              DiscountAmount: 0,
              DiscountInPercentage: 0,
              ProductKey: normalizedProductKey,
              DealID: modDealId,
              DealSlotID: modDealSlotId,
              UserAllergens: "",
              ModifierGroupID: modifierGroupInfo.ModifierGroupID,
              ModifierGroupName: modifierGroupInfo.ModifierGroupName,
            });
          });
        }
      });
 
      // Ensure every detail row carries a valid MenuItem_ID.
      // Backend detail tables expect MenuItem_ID on each row (items, variants, modifiers, ingredients, etc.).
      const toIntSafe = (v) => {
        const n = parseInt(v, 10);
        return isNaN(n) ? 0 : n;
      };
      const relationToPrimaryMenuId = new Map();
      orderDetails.forEach((d) => {
        const relationId = d?.ModifierRelationID;
        const menuId = toIntSafe(d?.MenuItem_ID);
        // Prefer non-modifier rows as the primary relation anchor.
        if (relationId && menuId > 0 && d?.MenuItem_IsModifier !== true && !relationToPrimaryMenuId.has(relationId)) {
          relationToPrimaryMenuId.set(relationId, menuId);
        }
      });

      orderDetails.forEach((d) => {
        let resolvedMenuId = toIntSafe(d?.MenuItem_ID);
        if (resolvedMenuId > 0) return;

        // Never assign the parent/base product MenuItem_ID to modifier lines. That was causing
        // POS/backoffice to treat every topping as the same product as the pizza (wrong grouping,
        // wrong sort order, "1 item" vs many identical base rows). Parent linkage stays on Parent_MenuItemID.
        const typeUpper = String(d?.MenuItem_Type || "").toUpperCase();
        if (typeUpper === "MODIFIER" && d?.MenuItem_IsModifier === true) {
          return;
        }

        const parentMenuId = toIntSafe(d?.Parent_MenuItemID);
        const relationMenuId = d?.ModifierRelationID ? toIntSafe(relationToPrimaryMenuId.get(d.ModifierRelationID)) : 0;
        const dealMenuId = toIntSafe(d?.DealID);

        resolvedMenuId =
          parentMenuId > 0
            ? parentMenuId
            : relationMenuId > 0
            ? relationMenuId
            : dealMenuId > 0
            ? dealMenuId
            : 0;

        d.MenuItem_ID = resolvedMenuId;
      });

      // Drop modifier/variant rows that still lack a catalog MenuItem_ID — these fail SP_InsertRecvOrderDetail
      // and previously left orphan Open orders in backoffice after the header insert succeeded.
      const invalidDetails = orderDetails.filter((d) => !(toIntSafe(d?.MenuItem_ID) > 0));
      if (invalidDetails.length > 0) {
        const dropTypes = new Set(["MODIFIER", "VARIANT"]);
        const droppable = [];
        const blocking = [];
        invalidDetails.forEach((d) => {
          const typeUpper = String(d?.MenuItem_Type || "").toUpperCase();
          if (dropTypes.has(typeUpper)) {
            droppable.push(d);
          } else {
            blocking.push(d);
          }
        });

        if (blocking.length > 0) {
          const names = blocking
            .map((d) => d.MenuItem_Name || d.MenuItem_Type || "item")
            .join(", ");
          throw new Error(
            `Cannot place order: missing product ID for ${names}. Please remove the item from your cart and add it again.`
          );
        }

        if (droppable.length > 0) {
          const dropSet = new Set(droppable);
          for (let i = orderDetails.length - 1; i >= 0; i -= 1) {
            if (dropSet.has(orderDetails[i])) {
              console.warn(
                `[Order] Removing ${orderDetails[i].MenuItem_Type} "${orderDetails[i].MenuItem_Name}" with invalid MenuItem_ID`
              );
              orderDetails.splice(i, 1);
            }
          }
        }
      }

      // Helper function to format order type with proper capitalization
      const formatOrderType = (type) => {
        if (!type) return "Delivery";
        const normalized = type.toLowerCase().trim();
        if (normalized === "dine-in" || normalized === "dinein") {
          return "Dine-In";
        } else if (normalized === "collection" || normalized === "pickup") {
          return "Collection";
        } else if (normalized === "delivery") {
          return "Delivery";
        }
        // Default: capitalize first letter of each word
        return type.split(/[\s-]+/).map(word => 
          word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        ).join("-");
      };

      const formattedOrderType = formatOrderType(orderType);
      const normalizedOrderType = String(orderType || "").toLowerCase().trim();
      const isCollectionOrder =
        normalizedOrderType === "collection" ||
        normalizedOrderType === "pickup" ||
        normalizedOrderType === "takeaway";
      const isScheduledOrderType = isDeliveryOrder || isCollectionOrder;

      const scheduledDate = String(pickupDate || "").trim();
      const scheduledTime = String(pickupTime || "").trim();
      const expectedDateTime = [scheduledDate, scheduledTime].filter(Boolean).join(" ").trim();
      const isPreOrder = isScheduledOrderType && Boolean(expectedDateTime);

      // Collection → Recvorder_ExpectedCollectionTime; delivery → Recvorder_ExpectedDeliveryTime
      const expectedDeliveryTime =
        isDeliveryOrder && expectedDateTime ? expectedDateTime : "";
      const expectedCollectionTime =
        isCollectionOrder && expectedDateTime ? expectedDateTime : "";

      // Order datetime (POS-compatible: ISO string for Recvorder_datetime)
      const orderDatetime = new Date().toISOString();

      // PaymentDetails only after real Dojo payment.
      // Unpaid pay-later orders: empty payment method (not "Cash"). Online checkout: "Online" while awaiting Dojo.
      const paymentDetails = [];

      ensureRecvorderDetailGuidsForPayload(orderDetails);

      // Build the full RecvOrder payload (POS-compatible structure: see docs/ORDER_PAYLOAD_STRUCTURE.md)
      const orderPayload = {
        Recvorder_ID: 0,
        Recvorder_datetime: orderDatetime,
        CompID: 0, // Will be set by backend from restaurant
        Customer_ID: (accountCustomerGuid || "").trim(),
        Customer_ContactNumber: customerPhone || "",
        CustomerGroupID: 0,
        CustomerGroupDiscountID: parseInt(orderCountDiscountId, 10) || 0,
        CustomerName: customerName || "",
        Recvorder_status: "Open",
        Recvorder_comments: orderComments || "",
        Recorder_Ackcomments: "",
        Recvorder_Totaldiscount:
          (parseFloat(voucherDiscount) || 0) +
          (parseFloat(rewardDiscount) || 0) +
          (parseFloat(orderCountDiscount) || 0),
        Recvorder_Servicecharges: parseFloat(serviceCharges) || 0,
        // Promotion code is only a loyalty reward signal for the API; amount is stored in Totaldiscount (POS-compatible).
        Recvorder_promotioncode: rewardId ? String(rewardId) : "",
        Recvorder_promotionDiscountAmount: 0,
        Recvorder_CustomerGroupDiscountAmount: parseFloat(orderCountDiscount) || 0,
        Recvorder_Totalamount: normalizedTotalAmount,
        Recvorder_Paymentmethod: awaitPayment ? "Online" : "",
        Recvorder_Paymentrecvstatus: "Pending",
        Recvorder_VoucherNo: voucherCode || "",
        Recvorder_ReceivedFrom: "Web",
        Recvorder_Ordertype: formattedOrderType,
        RecvOrder_DeliveryAddress: aptNo 
          ? `${customerAddress || ""}${customerAddress ? ", " : ""}Apt. ${aptNo}`.trim()
          : (customerAddress || ""),
        Recvorder_PostalCode: postalCode || "",
        Recvorder_InvoiceNo: awaitPayment ? "" : String(orderData.invoiceNo || orderData.InvoiceNo || "").trim(),
        Recvorder_Sync: false,
        VatAmount: parseFloat(vat) || 0,
        Recvorder_VATType: vatType || "inclusive",
        Recvorder_TipAmount: parseFloat(tip) || 0,
        Recvorder_ExtraCharges: 0,
        Recvorder_VocherDiscountAmount: parseFloat(voucherDiscount) || 0,
        // Loyalty reward discount is in Recvorder_Totaldiscount; promotioncode is only the reward ID for the API.
        Recvorder_OrderNatureType: "Online",
        Recvorder_OrderReceivedOnlineType: locationName || "Website",
        Recvorder_OrderSendBy: "WEB",
        Recvorder_PaymentBy: "",
        Recvorder_Deliverycharges: parseFloat(deliveryCharges) || 0,
        Recvorder_FrontOrderID: "",
        Recvorder_ReadyMins: 0,
        Recvorder_Servicechargespercentage: parseFloat(serviceChargesPercentage) || 0,
        Recvorder_Totaldiscountpercentage: 0,
        Recvorder_VATpercentage: parseFloat(vatPercentage) || 0,
        PrePayment: false,
        Recvorder_OnlineCharges: 0,
        Recvorder_OnlineChargesinPercentage: 0,
        Recvorder_ExpectedDeliveryTime: expectedDeliveryTime,
        Recvorder_ExpectedCollectionTime: expectedCollectionTime,
        Order_version: 1,
        IsSelfOrder: true,
        DeliveryAddressID: 0,
        SplitBillNo: 0,
        SplitType: "",
        SplitNoofCovers: 0,
        IsPaymentSplit: false,
        Tillname: "",
        TillID: "",
        AssignedRider: "",
        Isvoid: false,
        OrderTypeFlag: formattedOrderType,
        Ispreorder: isPreOrder,
        WaiterOrderStatus: "",
        IsOrderAccepted: false,
        // Dine-in floor / service area info
        OrderFloorName: serviceAreaName || "",
        OrderFloorId: serviceAreaId ? String(serviceAreaId) : "",
        PartySize: 0,
        IsVIPCustomer: false,
        OrderRejectorRefundReason: "",
        IsAddedInStats: false,
        SendByName: customerName || "",
        IsReservationOrder: false,
        ReservationID: "", // Must be string, not number (empty string for non-reservation orders)
        // T_Name = table display name (emails / backoffice UI). Recvorder_Tableid = numeric table id.
        T_Name: tableName || "",
        Recvorder_Tableid: tableId != null && String(tableId).trim() !== "" && String(tableId) !== "0"
          ? String(tableId)
          : "",
        ServiceAreaId: serviceAreaId || null,
        ServiceAreaName: serviceAreaName || "",
        MergeTableIds: "",
        OrderDetails: orderDetails,
        PaymentDetails: paymentDetails
      };

      return { orderPayload, locationId };
    } catch (error) {
      console.error("=== Error building order payload ===", error);
      throw error;
    }
  },

  /**
   * Asks the API what this cart actually costs, using the same pipeline that prices the order
   * at submit time (catalog reprice, deal slot rules, modifiers, VAT, service/delivery charges,
   * voucher, loyalty, order-count discount). Nothing is persisted.
   *
   * Showing and submitting this total is what keeps checkout off the "prices have changed" path:
   * the client no longer has to reproduce the server's money rules, and a price edited in local
   * storage is simply overwritten by the catalog price instead of being trusted.
   *
   * Never throws — callers fall back to their local estimate when a quote is unavailable.
   */
  async getPriceQuote(restaurantSlug, orderData) {
    try {
      const { orderPayload, locationId } = await this.buildOrderPayload(restaurantSlug, orderData);
      const response = await customerAxios.post(
        APIs.GET_ORDER_PRICE_QUOTE(restaurantSlug, locationId),
        orderPayload
      );

      const isSuccess = response.data?.status ?? response.data?.Status;
      const payload = response.data?.data || response.data?.Data || {};
      if (!isSuccess || payload.totalAmount === undefined || payload.totalAmount === null) {
        return {
          success: false,
          message: response.data?.message || response.data?.Message || "Could not price this order",
        };
      }

      const num = (value) => {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : 0;
      };

      return {
        success: true,
        total: num(payload.totalAmount),
        subtotal: num(payload.subtotal),
        taxableSubtotal: num(payload.taxableSubtotal),
        serviceCharges: num(payload.serviceCharges),
        serviceChargesPercentage: num(payload.serviceChargesPercentage),
        deliveryCharges: num(payload.deliveryCharges),
        vat: num(payload.vat),
        vatPercentage: num(payload.vatPercentage),
        vatType: payload.vatType || "exclusive",
        isTaxEnabled: payload.isTaxEnabled !== false,
        voucherDiscount: num(payload.voucherDiscount),
        rewardDiscount: num(payload.rewardDiscount),
        orderCountDiscount: num(payload.orderCountDiscount),
        totalDiscount: num(payload.totalDiscount),
        tipAmount: num(payload.tipAmount),
        currency: payload.currency || null,
        clientExpectedTotal: num(payload.clientExpectedTotal),
        totalChanged: payload.totalChanged === true,
        voucherCode: payload.voucherCode || "",
        voucherRejected: payload.voucherRejected === true,
        voucherMessage: payload.voucherMessage || null,
        rewardCode: payload.rewardCode || "",
        rewardRejected: payload.rewardRejected === true,
        rewardMessage: payload.rewardMessage || null,
        orderCountDiscountId: num(payload.orderCountDiscountId),
        linesSummary: payload.linesSummary || null,
      };
    } catch (error) {
      const data = error?.response?.data;
      return {
        success: false,
        message:
          data?.message ||
          data?.Message ||
          error?.message ||
          "Could not price this order",
      };
    }
  },

  /**
   * Quote, then place. Submitting the freshly quoted total is what keeps a legitimate cart off the
   * submit-time total check for callers that place an order without a checkout review step.
   *
   * When the quote disagrees with what the guest was actually shown, the order is NOT placed —
   * the caller gets `priceChangedBeforeSubmit` so it can send them somewhere they can review the
   * new total rather than being charged an amount they never saw.
   */
  async submitOrderWithServerTotal(restaurantSlug, orderData, { tolerance = 0.02 } = {}) {
    const quote = await this.getPriceQuote(restaurantSlug, orderData);
    if (!quote.success) {
      // Pricing unavailable — fall through and let the submit endpoint be the judge.
      return this.submitOrder(restaurantSlug, orderData);
    }

    const shownTotal = Number(orderData?.totalAmount) || 0;
    if (shownTotal > 0 && Math.abs(shownTotal - quote.total) > tolerance) {
      return {
        success: false,
        priceChangedBeforeSubmit: true,
        serverTotal: quote.total,
        message: quote.voucherMessage || quote.rewardMessage || "Prices for your items have been updated.",
      };
    }

    return this.submitOrder(restaurantSlug, { ...orderData, totalAmount: quote.total });
  },

  async submitOrder(restaurantSlug, orderData) {
    console.log("=== onlineOrderingService.submitOrder called ===");
    try {
      const { orderPayload, locationId } = await this.buildOrderPayload(restaurantSlug, orderData);
      const apiUrl = APIs.SUBMIT_ONLINE_ORDER(restaurantSlug, locationId);
      console.log("API URL:", apiUrl);
      console.log("Order payload:", JSON.stringify(orderPayload, null, 2));
      
      let response;
      try {
        response = await customerAxios.post(apiUrl, orderPayload);
      } catch (axiosError) {
        const mismatch = extractPriceMismatch(axiosError);
        if (mismatch) return mismatch;

        // If axios throws an error (network, timeout, etc.), preserve the error
        console.error("=== Axios error occurred ===");
        console.error("Error message:", axiosError.message);
        console.error("Error response:", axiosError.response?.data || axiosError.response);
        console.error("Error request:", axiosError.request ? "Request was made" : "No request object");
        console.error("Error config:", axiosError.config?.url);
        
        // Create an error with more context
        const networkError = new Error(axiosError.response?.data?.message || axiosError.response?.data?.Message || axiosError.message || "Network error: Failed to submit order");
        networkError.response = axiosError.response;
        networkError.request = axiosError.request;
        networkError.config = axiosError.config;
        throw networkError;
      }

      // Check HTTP status first - 400+ means error
      if (response.status >= 400) {
        const mismatch = extractPriceMismatch(response);
        if (mismatch) return mismatch;

        const errorMessage = response.data?.message || response.data?.Message || `Failed to submit order (HTTP ${response.status})`;
        console.error("API returned error status:", response.status);
        console.error("Error response data:", response.data);
        
        // Create an error that preserves the response
        const apiError = new Error(errorMessage);
        apiError.response = response;
        apiError.status = response.status;
        throw apiError;
      }

      // Check response body status (handle both camelCase and PascalCase)
      const isSuccess = response.data?.status ?? response.data?.Status;
      if (isSuccess) {
        return {
          success: true,
          message: response.data?.message || response.data?.Message || "Order submitted successfully",
          data: response.data?.data || response.data?.Data,
          invoiceNo: response.data?.data?.invoiceNo || response.data?.Data?.InvoiceNo,
          frontOrderId:
            response.data?.data?.frontOrderId ||
            response.data?.Data?.FrontOrderId ||
            response.data?.data?.FrontOrderID ||
            null,
        };
      } else {
        const errorMessage = response.data?.message || response.data?.Message || "Failed to submit order";
        console.error("API returned unsuccessful status in response body");
        console.error("Response data:", response.data);
        
        // Create an error that preserves the response
        const apiError = new Error(errorMessage);
        apiError.response = response;
        throw apiError;
      }
    } catch (error) {
      const mismatch = extractPriceMismatch(error);
      if (mismatch) return mismatch;

      console.error("=== Error submitting online order ===");
      console.error("Error message:", error.message);
      
      // Check if it's an axios error or our custom error with response
      if (error.response) {
        console.error("Error response status:", error.response.status);
        console.error("Error response data:", error.response.data);
      } else {
        console.error("No response object in error (might be network error)");
      }
      
      if (error.request) {
        console.error("Error request exists (request was made but no response received)");
      } else {
        console.error("No request object in error");
      }
      
      if (error.config) {
        console.error("Request URL:", error.config.url);
        console.error("Request method:", error.config.method);
      }
      
      console.error("Full error:", error);
      
      // If error doesn't have a message, provide a default one
      if (!error.message || error.message === "Failed to submit order") {
        if (error.response) {
          error.message = error.response.data?.message || error.response.data?.Message || `Failed to submit order (HTTP ${error.response.status})`;
        } else if (error.request) {
          error.message = "Network error: Request was made but no response received from server";
        } else {
          error.message = "Failed to submit order. Please check your connection and try again.";
        }
      }
      
      throw error;
    }
  },

  async getPaymentConfig(restaurantSlug, locationId = null) {
    try {
      const response = await publicAxios.get(APIs.GET_PAYMENT_CONFIG(restaurantSlug, locationId));
      const data = response.data?.data || response.data?.Data || {};
      return {
        paymentRequired: !!(data.paymentRequired ?? data.PaymentRequired),
        collectionPaymentRequired: !!(
          data.collectionPaymentRequired ?? data.CollectionPaymentRequired
        ),
        provider: data.provider ?? data.Provider ?? null,
        currency: data.currency ?? data.Currency ?? "GBP",
        mode: data.mode ?? data.Mode ?? "hosted",
      };
    } catch (error) {
      console.error("Error fetching payment config:", error);
      return {
        paymentRequired: false,
        collectionPaymentRequired: false,
        provider: null,
        currency: "GBP",
        mode: "hosted",
      };
    }
  },

  async prepareCheckout(restaurantSlug, orderData) {
    try {
      const { orderPayload, locationId } = await this.buildOrderPayload(restaurantSlug, orderData, {
        awaitPayment: true,
      });
      const returnBaseUrl =
        typeof window !== "undefined" && window.location?.origin
          ? window.location.origin
          : null;
      const apiUrl = APIs.PREPARE_CHECKOUT(restaurantSlug, locationId, returnBaseUrl);
      let response;
      try {
        response = await customerAxios.post(apiUrl, orderPayload);
      } catch (axiosError) {
        const mismatch = extractPriceMismatch(axiosError);
        if (mismatch) return mismatch;
        throw axiosError;
      }
      if (response.status >= 400) {
        const mismatch = extractPriceMismatch(response);
        if (mismatch) return mismatch;
        throw new Error(response.data?.message || response.data?.Message || "Failed to prepare checkout");
      }
      const isSuccess = response.data?.status ?? response.data?.Status;
      if (!isSuccess) {
        throw new Error(response.data?.message || response.data?.Message || "Failed to prepare checkout");
      }
      const payload = response.data?.data || response.data?.Data || {};
      return {
        success: true,
        sessionId: payload.sessionId || payload.SessionId,
        checkoutUrl: payload.checkoutUrl || payload.CheckoutUrl,
        paymentIntentId: payload.paymentIntentId || payload.PaymentIntentId,
        expiresAt: payload.expiresAt || payload.ExpiresAt,
        totalAmount: payload.totalAmount ?? payload.TotalAmount,
        invoiceNo: payload.invoiceNo || payload.InvoiceNo || null,
      };
    } catch (error) {
      const mismatch = extractPriceMismatch(error);
      if (mismatch) return mismatch;
      console.error("Error preparing checkout:", error);
      throw error;
    }
  },

  async getCheckoutStatus(sessionId) {
    try {
      const response = await publicAxios.get(APIs.GET_CHECKOUT_STATUS(sessionId));
      const payload = response.data?.data || response.data?.Data || {};
      return {
        success: true,
        status: payload.status || payload.Status,
        invoiceNo: payload.invoiceNo || payload.InvoiceNo,
        frontOrderId: payload.frontOrderId || payload.FrontOrderId,
        sessionId: payload.sessionId || payload.SessionId || sessionId,
        completionError: payload.completionError || payload.CompletionError || null,
      };
    } catch (error) {
      console.error("Error fetching checkout status:", error);
      throw error;
    }
  },

  async finalizeCheckout(sessionId) {
    try {
      const response = await publicAxios.post(APIs.FINALIZE_CHECKOUT(sessionId));
      const payload = response.data?.data || response.data?.Data || {};
      return {
        success: true,
        status: payload.status || payload.Status,
        invoiceNo: payload.invoiceNo || payload.InvoiceNo,
        frontOrderId: payload.frontOrderId || payload.FrontOrderId,
        sessionId: payload.sessionId || payload.SessionId || sessionId,
        completionError: payload.completionError || payload.CompletionError || null,
      };
    } catch (error) {
      console.error("Error finalizing checkout:", error);
      throw error;
    }
  },

  /**
   * Resolve the postcode-based delivery fee.
   * Returns { fee, ruleID, matchType, pattern, matched } where `matched` is
   * false when the company-default fallback was used.
   * Always returns an object (defaults to 0 when the API errors).
   */
  async quoteDeliveryCharge(compId, postalCode) {
    try {
      if (!compId) {
        return { fee: 0, ruleID: null, matchType: null, pattern: null, matched: false };
      }
      // Public anonymous endpoint — use public client so BackOffice auth cookies/tokens
      // cannot interfere with storefront quoting.
      const response = await publicAxios.get(
        APIs.QUOTE_DELIVERY_CHARGE(compId, postalCode || "")
      );
      if (response.status >= 400 || response.data?.status === false) {
        console.warn("Delivery charge quote failed:", response.status, response.data?.message);
        return { fee: 0, ruleID: null, matchType: null, pattern: null, matched: false };
      }
      const data = response.data?.data || response.data?.Data || {};
      const feeRaw = data.fee ?? data.Fee ?? 0;
      const fee = typeof feeRaw === "number" ? feeRaw : parseFloat(feeRaw);
      return {
        fee: Number.isFinite(fee) ? fee : 0,
        ruleID: data.ruleID ?? data.RuleID ?? null,
        matchType: data.matchType ?? data.MatchType ?? null,
        pattern: data.pattern ?? data.Pattern ?? null,
        matched: !!(data.matched ?? data.Matched),
      };
    } catch (error) {
      console.error("Error resolving delivery charge:", error);
      return { fee: 0, ruleID: null, matchType: null, pattern: null, matched: false };
    }
  }
};

export default onlineOrderingService;
